import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import API from '../api';
import ExcelJS from 'exceljs/dist/exceljs.min.js';
import { usePermissions } from '../hooks/usePermissions';
import { logDownload, logPrint } from '../utils/eventLog';
import { printedByLabel } from '../utils/userLabel';

const COLOR_STYLE = {
  BK:{ bg:'#222', color:'#fff' }, BL:{ bg:'#1d4ed8', color:'#fff' },
  RD:{ bg:'#dc2626', color:'#fff' }, OG:{ bg:'#ea580c', color:'#fff' },
  GF:{ bg:'#84cc16', color:'#fff' }, YE:{ bg:'#facc15', color:'#222' },
};
// 모델명 목록(MODELS)은 더 이상 하드코딩하지 않고 재고관리에 등록된 완제품을 그대로 씁니다
// (PlanForm 참고). 옵션(색상 등 상세옵션) 목록도 더 이상 하드코딩하지 않고, 재고관리
// "목록"(완제품 stock_columns)에 실제 등록된 항목을 그대로 씁니다.

// 단계 색상
const STAGE_COLOR = { '계획':'#854F0B', '진행':'#185FA5', '보류':'#993556' };
const STAGE_BG    = { '계획':'#FAEEDA', '진행':'#E6F1FB', '보류':'#FBEAF0' };

// 진행현황 색상
const PROG_COLOR = {
  '생산계획':'#555', '관리승인':'#854F0B', '생산승인':'#0F6E56',
  '생산진행':'#185FA5', '생산보류':'#993556', '생산완료':'#374151'
};
const PROG_BG = {
  '생산계획':'#f0f0f0', '관리승인':'#FAEEDA', '생산승인':'#E1F5EE',
  '생산진행':'#E6F1FB', '생산보류':'#FBEAF0', '생산완료':'#F3F4F6'
};

const formatNum = n => (n||0).toLocaleString();

// D-Day 계산
const calcDday = (dateStr) => {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0,0,0,0);
  const target = new Date(dateStr); target.setHours(0,0,0,0);
  return Math.ceil((target - today) / (1000*60*60*24));
};

// 진행중 D-Day 뱃지 (시작일 기준)
const DdayBadge = ({ date }) => {
  const d = calcDday(date);
  if (d === null) return <span style={{ fontSize:12, color:'#aaa' }}>-</span>;
  const color = d<0?'#dc2626':d<=3?'#d97706':'#185FA5';
  const bg    = d<0?'#fee2e2':d<=3?'#fef3c7':'#E6F1FB';
  const label = d<0?`D+${Math.abs(d)}`:d===0?'D-Day':`D-${d}`;
  return <span style={{ fontSize:13, padding:'3px 13px', borderRadius:20,
    background:bg, color, fontWeight:700 }}>{label}</span>;
};

// 완료일 셀
// - 진행중: 목표일(endDate) 기준 D-Day
// - 완료: 실제완료일(YYYY-MM-DD) + 목표일 대비 차이 뱃지 (파란=일찍, 빨강=늦음)
const CompletedDateCell = ({ startDate, endDate, completedAt }) => {
  if (!completedAt) {
    // 진행중: 목표일 기준 D-Day
    return <DdayBadge date={endDate} />;
  }
  // 완료: parseDate로 정확히 파싱
  const completedDay = parseDate(completedAt);
  const diff  = calcDday2(endDate, completedDay); // 양수=일찍완료, 음수=늦게완료
  const early = diff > 0;
  const label = diff === 0 ? '당일완료' : early ? `D-${diff}` : `D+${Math.abs(diff)}`;
  const color = early || diff===0 ? '#185FA5' : '#dc2626';
  const bg    = early || diff===0 ? '#E6F1FB'  : '#fee2e2';
  return (
    <div style={{ lineHeight:1.8 }}>
      <div style={{ fontSize:13, color:'#333', fontWeight:600, whiteSpace:'nowrap' }}>{completedDay}</div>
      <span style={{ fontSize:13, padding:'3px 13px', borderRadius:20,
        background:bg, color, fontWeight:700 }}>{label}</span>
    </div>
  );
};

// 날짜 표시 통일: '2026-07-28 14:31:44' → '2026. 07. 28. 14:31:44'
const formatDT = (str) => {
  if (!str) return '';
  const s = str.trim();
  // 이미 '. ' 형식이면 그대로
  if (s.includes('. ')) return s;
  // 'YYYY-MM-DD HH:MM:SS' 형식 변환 (초까지 포함)
  const m = s.match(/(\d{4})-(\d{2})-(\d{2})[\sT](\d{2}:\d{2}:\d{2})/);
  if (m) return `${m[1]}. ${m[2]}. ${m[3]}. ${m[4]}`;
  // 초 없으면 HH:MM만
  const m2 = s.match(/(\d{4})-(\d{2})-(\d{2})[\sT](\d{2}:\d{2})/);
  if (m2) return `${m2[1]}. ${m2[2]}. ${m2[3]}. ${m2[4]}`;
  return s;
};

// 날짜 파싱 헬퍼: '2026. 07. 28. 09:41:07' 또는 '2026-07-28' → 'YYYY-MM-DD'
const parseDate = (str) => {
  if (!str) return '';
  const s = str.trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0,10);
  const m = s.match(/(\d{4})\.\s*(\d{1,2})\.\s*(\d{1,2})/);
  if (m) {
    const pad = n => String(n).padStart(2,'0');
    return m[1]+'-'+pad(m[2])+'-'+pad(m[3]);
  }
  return s.slice(0,10);
};

// 두 날짜 차이 계산 (endDate - completedDate, 양수면 일찍 완료)
const calcDday2 = (endDate, completedDate) => {
  if (!endDate || !completedDate) return 0;
  const end  = new Date(endDate);       end.setHours(0,0,0,0);
  const done = new Date(completedDate); done.setHours(0,0,0,0);
  return Math.ceil((end - done) / (1000*60*60*24));
};

const parseJSON = (str, fb=[]) => { try { return JSON.parse(str); } catch { return str?[str]:fb; } };

// ── 생산계획 비고(note) 헬퍼 ──────────────────────────
// note는 등록/관리승인/생산승인/보류 등 이벤트가 있을 때마다 한 줄씩 이어붙여집니다
// (예: "생산계획 : 홍길동 (2026. 08. 08. 10:00:00)\n<특이사항>\n관리승인 : ... \n생산승인 : ...").
// 비고 열에는 그중 가장 최근(마지막) 줄만 보여줘서 "최신 업데이트 사항"만 한눈에 보이게 합니다.
const getLatestNoteLine = (note) => {
  if (!note) return '-';
  const lines = String(note).split('\n').map(l => l.trim()).filter(Boolean);
  return lines.length ? lines[lines.length - 1] : '-';
};
// 등록 시 사용자가 직접 입력한 "특이사항"만 뽑아냅니다 — note의 두 번째 줄이 시스템이
// 자동으로 붙인 관리승인/생산승인/보류사유 줄이 아닐 때만 특이사항으로 인정합니다.
const getOriginalRemark = (note) => {
  if (!note) return '';
  const lines = String(note).split('\n').map(l => l.trim()).filter(Boolean);
  if (lines.length < 2) return '';
  const candidate = lines[1];
  const isSystemLine = /^(관리승인|생산승인)\s*:/.test(candidate) || /^\[보류사유:/.test(candidate);
  return isSystemLine ? '' : candidate;
};

// ── 엑셀 리포트용 헬퍼 ──────────────────────────────
const pad2 = n => String(n).padStart(2,'0');

// '2026. 07. 28. 14:31:20' 또는 '2026-07-28 14:31:20' → Date 객체
const toDateObj = (str) => {
  if (!str) return null;
  const s = str.trim();
  let m = s.match(/(\d{4})\.\s*(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (m) return new Date(+m[1], +m[2]-1, +m[3], +m[4], +m[5], +(m[6]||0));
  m = s.match(/(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (m) return new Date(+m[1], +m[2]-1, +m[3], +m[4], +m[5], +(m[6]||0));
  m = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(+m[1], +m[2]-1, +m[3]);
  return null;
};
// 짧은 형식: '07.28 14:31' (승인 타임라인용)
const fmtShort = (str) => {
  const d = toDateObj(str);
  if (!d) return '-';
  return `${pad2(d.getMonth()+1)}.${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};
// 긴 형식: '2026-07-28 19:24' (최종 업데이트용)
const fmtLong = (str) => {
  const d = toDateObj(str);
  if (!d) return '-';
  return `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};
// 잔여일 라벨: 완료 시 목표일 대비 조기/지연, 진행중이면 목표일 기준 D-day
const remainLabel = (s) => {
  if (s.completed_at) {
    const diff = calcDday2(s.end_date, parseDate(s.completed_at));
    if (diff === 0) return '당일완료';
    return diff > 0 ? `D-${diff}` : `D+${Math.abs(diff)}`;
  }
  const d = calcDday(s.end_date);
  if (d === null) return '-';
  return d < 0 ? `D+${Math.abs(d)}` : d === 0 ? 'D-Day' : `D-${d}`;
};

const cellHead = {
  padding:'10px 8px', background:'#f8fafc', fontSize:14, fontWeight:600,
  color:'#555', border:'1px solid #e5e7eb', textAlign:'center', whiteSpace:'nowrap'
};
const cellBody = {
  padding:'10px 8px', fontSize:14, border:'1px solid #e5e7eb',
  textAlign:'center', verticalAlign:'middle'
};

// 목록 컬럼명 클릭 정렬(오름/내림차순 토글) — 생산계획/생산현황 표 공용.
// 인사관리(HRManagement.js)의 컬럼명 클릭 정렬과 동일한 방식: 숫자면 숫자비교, 아니면 문자열비교.
function sortRows(rows, sortKey, sortDir) {
  if (!sortKey) return rows;
  const copy = [...rows];
  copy.sort((a, b) => {
    let va = a ? a[sortKey] : null;
    let vb = b ? b[sortKey] : null;
    if (va === null || va === undefined) va = '';
    if (vb === null || vb === undefined) vb = '';
    let cmp;
    const na = Number(va), nb = Number(vb);
    if (va !== '' && vb !== '' && !isNaN(na) && !isNaN(nb)) cmp = na - nb;
    else cmp = String(va).localeCompare(String(vb), 'ko');
    return sortDir === 'asc' ? cmp : -cmp;
  });
  return copy;
}
// 목록 헤더 <th> — 컬럼명을 클릭할 때마다 오름차순/내림차순이 토글된다(활성 컬럼만 색상 강조).
// key가 없으면(버튼/뱃지 등 정렬 불가 컬럼) 클릭 불가능한 일반 <th>로 표시한다.
function SortableTh({ label, sortKey, activeKey, dir, onSort, style }) {
  if (!sortKey) return <th style={style || cellHead}>{label}</th>;
  const active = activeKey === sortKey;
  return (
    <th style={{ ...(style || cellHead), cursor:'pointer', userSelect:'none', color: active ? '#185FA5' : (style||cellHead).color }}
      title={active ? (dir === 'asc' ? '오름차순 (클릭시 내림차순)' : '내림차순 (클릭시 오름차순)') : '클릭하여 정렬'}
      onClick={() => onSort(sortKey)}>
      {label}
    </th>
  );
}

export default function Productions() {
  const [plans, setPlans]           = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [allStatusList, setAllStatusList] = useState([]); // confirmed 포함 전체
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [editPlan, setEditPlan]     = useState(null);
  const [actionPlan, setActionPlan] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showHoldModal, setShowHoldModal] = useState(null); // 보류 사유 모달
  const [msg, setMsg]               = useState('');
  const [msgType, setMsgType]       = useState('success');
  // 목록 컬럼명 클릭 정렬 — 생산계획/생산현황 표 각각 독립적으로 상태를 갖는다.
  const [planSortKey, setPlanSortKey] = useState(null);
  const [planSortDir, setPlanSortDir] = useState('asc');
  const togglePlanSort = (key) => {
    if (planSortKey === key) setPlanSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setPlanSortKey(key); setPlanSortDir('asc'); }
  };
  const [statusSortKey, setStatusSortKey] = useState(null);
  const [statusSortDir, setStatusSortDir] = useState('asc');
  const toggleStatusSort = (key) => {
    if (statusSortKey === key) setStatusSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setStatusSortKey(key); setStatusSortDir('asc'); }
  };

  // 재고관리에서 등록한 제품군(안전벨트/안전블럭 등) — 생산계획의 모델명은 이 중 선택된
  // 제품군에 재고관리로 등록된 완제품 목록을 그대로 가져와 사용합니다.
  const [groups, setGroups]   = useState([]);
  const [groupTab, setGroupTab] = useState(null); // null = 전체(모든 제품군)
  const loadGroups = () => API.get('/productions/product-groups').then(r => setGroups(r.data)).catch(()=>{});

  // 제품군이 1개뿐이면 "전체"와 그 제품군이 사실상 같은 화면이라 탭 자체가 필요 없다 —
  // 굳이 전체 화면을 거치지 않고 바로 그 제품군을 선택한 것처럼 카드 수치를 보여주고,
  // 아래 탭 목록도 숨긴다(렌더링 쪽 gate: groups.length > 1). 제품군이 2개 이상이면
  // 기존처럼 groupTab은 null(전체)로 시작하는 동작을 그대로 유지한다.
  useEffect(() => {
    if (groups.length === 1) setGroupTab(groups[0].id);
  }, [groups]);

  // 생산현황 월 이동 / 전체목록(완료확인 포함) 토글 / 내보내기 모달
  const nowDate                       = new Date();
  const [viewYear, setViewYear]       = useState(nowDate.getFullYear());
  const [viewMonth, setViewMonth]     = useState(nowDate.getMonth()+1);
  const [showAllConfirmed, setShowAllConfirmed] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [pickerYear, setPickerYear] = useState(nowDate.getFullYear());
  const datePickerRef = useRef(null);
  const [showExportModal, setShowExportModal]   = useState(false);
  const [exporting, setExporting]     = useState(false);

  const isCurrentMonthView = !!viewMonth && viewYear===nowDate.getFullYear() && viewMonth===(nowDate.getMonth()+1);
  const isCurrentYearView  = !viewMonth && viewYear===nowDate.getFullYear();
  // 진행중 항목도 함께 보여줄지 여부 — 이번달을 보고 있거나, 올해를 통째로(연도 전체보기) 보고 있을 때
  const includeInProgress  = isCurrentMonthView || isCurrentYearView;

  const user         = JSON.parse(sessionStorage.getItem('user') || '{}');
  const isSuperAdmin = user.role === 'superadmin' || user.role === 'admin';

  // 관리자/생산관리자/협력사 역할 개념이 없어졌으므로, 최고관리자 외에는 예외 없이
  // 계정관리 > 권한설정에서 부여한 값을 그대로 따릅니다(hasDetail/canWriteMenu가 내부적으로
  // 최고관리자 여부를 이미 처리합니다).
  const { hasDetail, canWrite: canWriteMenu } = usePermissions();
  const canEdit              = canWriteMenu('productions'); // 계획 수정
  // 관리승인/생산승인 버튼 노출 여부 — 예전엔 실행권한과 별도로 "표시" 전용 권한을 따로
  // 관리했지만, 실행권한만 있으면 어차피 버튼을 눌러 실행할 수 있으니 표시를 따로 숨길 이유가
  // 없어 그 표시 전용 권한 레이어는 폐지했습니다. 이제 버튼 노출 = 실행권한
  // (admin_approval_action/production_approval_action) 그대로입니다.
  const canSeeAdminApproval  = hasDetail('productions', 'admin_approval_action');
  const canSeeWorkerApproval = hasDetail('productions', 'production_approval_action');
  const canRegister   = hasDetail('productions', 'plan_register_allowed');
  const canDeletePlan = isSuperAdmin; // 삭제는 최고관리자만
  const canSeePlanList   = hasDetail('productions', 'plan_list_visible');
  const canSeeStatusList = hasDetail('productions', 'status_list_visible');
  // "생산계획 상세보기" — 생산계획 목록에서 계획 상세(StatusDetail 모달)를 열 수 있는지.
  // 생산현황 목록의 상세보기(status_detail_visible, StatusRow에서 별도 체크)와는 서로 다른
  // 진입점이라 독립된 키로 관리합니다 — 둘 다 결과적으로 같은 showDetail/selectedPlan
  // 상태를 여는 동일한 모달을 쓰지만, 어느 목록에서 열었는지에 따라 필요한 권한이 다릅니다.
  const canSeePlanDetail = hasDetail('productions', 'plan_detail_visible');
  // "생산현황 삭제" 권한(status_delete_action)을 받은 사람은 최고관리자가 아니어도 삭제요청을
  // 승인/거부할 수 있다 — 백엔드 /delete-requests 계열 라우트도 동일한 권한으로 검사한다.
  const canApproveDeleteRequests = isSuperAdmin || hasDetail('productions', 'status_delete_action');
  const [showDeleteRequests, setShowDeleteRequests] = useState(false);
  const [deleteReqCount, setDeleteReqCount] = useState(0);

  const loadDeleteReqCount = () => {
    if (!canApproveDeleteRequests) return;
    API.get('/productions/delete-requests').then(r => setDeleteReqCount(r.data.length)).catch(()=>{});
  };

  const loadPlans  = () => API.get('/productions/plans').then(r => setPlans(r.data)).catch(()=>{});
  // month가 없으면(연도 전체보기) year 파라미터로 조회 — 서버가 해당 연도에 완료된 항목 전체를 내려줌
  const loadStatus = (year=viewYear, month=viewMonth) => {
    const params = month ? { month: year+'-'+String(month).padStart(2,'0') } : { year: String(year) };
    API.get('/productions/status', { params }).then(r => {
      setAllStatusList(r.data);                        // 전체 (누적생산 카드/전체목록용)
      setStatusList(r.data.filter(s => !s.confirmed)); // confirmed 제외 (기본 목록용)
    }).catch(()=>{});
  };

  useEffect(() => { loadPlans(); loadStatus(viewYear, viewMonth); loadDeleteReqCount(); loadGroups(); }, [viewYear, viewMonth]);

  // 화면 하단 "최신글/업데이트" 활동피드 바에서 생산관리 항목을 클릭해 들어온 경우, 그 생산계획의
  // 상세 모달을 바로 연다. plans가 아직 로드되기 전(마운트 직후)일 수 있으므로 로드될 때까지 대기.
  const feedLocation = useLocation();
  const feedNavigate = useNavigate();
  useEffect(() => {
    const ao = feedLocation.state?.activityOpen;
    if (ao && plans.length) {
      const found = plans.find(p => String(p.id) === String(ao.itemId));
      if (found) { setSelectedPlan(found); setShowDetail(true); }
      feedNavigate(feedLocation.pathname, { replace: true, state: {} });
    }
  }, [feedLocation.state, plans]); // eslint-disable-line

  // 연/월 선택 팝오버: 열릴 때 현재 조회연도로 초기화 + 바깥 클릭 시 닫기
  useEffect(() => { if (showDatePicker) setPickerYear(viewYear); }, [showDatePicker, viewYear]);
  useEffect(() => {
    if (!showDatePicker) return;
    const onClickOutside = (e) => { if (datePickerRef.current && !datePickerRef.current.contains(e.target)) setShowDatePicker(false); };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [showDatePicker]);

  // 이전달/다음달 이동 (연도 전체보기 중에는 월 단위 이동 버튼 자체가 비활성화되어 있음)
  const goPrevMonth = () => {
    if (!viewMonth) return;
    if (viewMonth===1) { setViewYear(y=>y-1); setViewMonth(12); }
    else setViewMonth(m=>m-1);
  };
  const goNextMonth = () => {
    if (!viewMonth || isCurrentMonthView) return;
    if (viewMonth===12) { setViewYear(y=>y+1); setViewMonth(1); }
    else setViewMonth(m=>m+1);
  };
  const goThisMonth = () => { setViewYear(nowDate.getFullYear()); setViewMonth(nowDate.getMonth()+1); };

  // 목록 버튼 토글에 따라 사용할 생산현황 리스트 (confirmed 포함 여부)
  const activeStatusList = showAllConfirmed ? allStatusList : statusList;

  const showMsg = (text, type='success') => {
    setMsg(text); setMsgType(type);
    setTimeout(()=>setMsg(''), 4000);
  };

  // 진행현황 변경
  const handleProgress = async (plan, nextProgress, note='') => {
    try {
      const res = await API.patch('/productions/plans/'+plan.id+'/progress', { progress: nextProgress, note });
      showMsg(res.data.message);
      loadPlans(); loadStatus();
    } catch(err) { showMsg(err.response?.data?.message||'오류','error'); }
  };

  const deletePlan = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    await API.delete('/productions/plans/'+id);
    setActionPlan(null); showMsg('삭제 완료!'); loadPlans();
  };

  // 선택한 연/월의 생산현황을 "통합 현황 보고서" 형태의 엑셀로 내보내기
  const handleExport = async (year, month) => {
    setExporting(true);
    try {
      const ym = year + '-' + pad2(month);
      const [statusRes, logsRes] = await Promise.all([
        API.get('/productions/status', { params:{ month: ym } }),
        API.get('/productions/logs/month', { params:{ year, month } })
      ]);
      const items = statusRes.data || [];
      const logs  = logsRes.data || [];
      const logsByStatus = {};
      logs.forEach(l => { (logsByStatus[l.status_id] = logsByStatus[l.status_id] || []).push(l); });

      const wb = new ExcelJS.Workbook();
      const sheetName = `${year}년 ${pad2(month)}월`;
      const ws = wb.addWorksheet(sheetName);

      const cols = [
        { header:'구분',           width:11 },
        { header:'진행현황',       width:11 },
        { header:'담당자',         width:10 },
        { header:'모델명',         width:16 },
        { header:'옵션',           width:8  },
        { header:'목표수량',       width:10 },
        { header:'생산수량',       width:10 },
        { header:'공정률',         width:9  },
        { header:'시작일',         width:12 },
        { header:'목표일',         width:12 },
        { header:'잔여일',         width:9  },
        { header:'생산계획 등록',  width:18 },
        { header:'관리승인',       width:18 },
        { header:'생산승인',       width:18 },
        { header:'최종 업데이트',  width:17 },
        { header:'작업자',         width:10 },
        { header:'수량 변경 이력', width:16 },
        { header:'최종 증감',      width:10 },
      ];
      ws.columns = cols.map(c => ({ width: c.width }));
      const totalCols = cols.length;
      const thin = { style:'thin', color:{argb:'FFE5E7EB'} };

      // 제목
      ws.mergeCells(1,1,1,totalCols);
      const titleCell = ws.getCell(1,1);
      titleCell.value = '생산관리 및 공정·업데이트 통합 현황 보고서';
      titleCell.font = { size:16, bold:true, color:{argb:'FF1A1A1A'} };
      ws.getRow(1).height = 28;

      // 부제 (기준일시)
      ws.mergeCells(2,1,2,totalCols);
      const now = new Date();
      const subCell = ws.getCell(2,1);
      subCell.value = `기준일시: ${now.getFullYear()}-${pad2(now.getMonth()+1)}-${pad2(now.getDate())} ${pad2(now.getHours())}:${pad2(now.getMinutes())}  |  생성: 시스템 자동 내보내기`;
      subCell.font = { size:10, italic:true, color:{argb:'FF888888'} };

      ws.addRow([]); // 빈 행

      // 그룹 헤더 (① / ② / ③)
      const groupRowIdx = 4;
      ws.mergeCells(groupRowIdx,1,groupRowIdx,11);
      ws.mergeCells(groupRowIdx,12,groupRowIdx,14);
      ws.mergeCells(groupRowIdx,15,groupRowIdx,18);
      ws.getCell(groupRowIdx,1).value  = '① 기본 생산 현황 정보';
      ws.getCell(groupRowIdx,12).value = '② 승인 프로세스 타임라인';
      ws.getCell(groupRowIdx,15).value = '③ 최근 수량 변경 실적 로그';
      for (let c=1; c<=totalCols; c++) {
        const cell = ws.getCell(groupRowIdx, c);
        const color = c<=11 ? 'FF1F3B66' : c<=14 ? 'FF0F6E56' : 'FFC2680D';
        cell.fill = { type:'pattern', pattern:'solid', fgColor:{argb:color} };
        cell.font = { bold:true, color:{argb:'FFFFFFFF'}, size:12 };
        cell.alignment = { horizontal:'center', vertical:'middle' };
      }
      ws.getRow(groupRowIdx).height = 24;

      // 컬럼 헤더
      const headerRowIdx = 5;
      const headerRow = ws.getRow(headerRowIdx);
      cols.forEach((c,i) => { headerRow.getCell(i+1).value = c.header; });
      headerRow.eachCell(cell => {
        cell.font = { bold:true, color:{argb:'FFFFFFFF'}, size:11 };
        cell.alignment = { horizontal:'center', vertical:'middle' };
        cell.fill = { type:'pattern', pattern:'solid', fgColor:{argb:'FF1E293B'} };
      });
      headerRow.height = 22;

      // 데이터 행
      let r = headerRowIdx + 1;
      items.forEach(s => {
        const plan = plans.find(p => p.id === s.plan_id);
        const itemLogs = (logsByStatus[s.id] || [])
          .slice()
          .sort((a,b) => (a.updated_at||'').localeCompare(b.updated_at||''));
        const rowCount  = itemLogs.length || 1;
        const startRow  = r;
        const endRow    = r + rowCount - 1;
        const highlight = !!(plan && (plan.admin_confirmed_by || plan.worker_confirmed_by));
        const remain    = remainLabel(s);

        const baseVals = [
          s.category, s.status, s.manager||'-', s.model_no, s.color,
          s.target_qty, s.produced_qty, (s.progress_rate||0).toFixed(1)+'%',
          s.start_date||'-', s.end_date||'-', remain
        ];
        const approvalVals = [
          plan?.created_by        ? `${plan.created_by} (${fmtShort(plan.created_at)})`               : '-',
          plan?.admin_confirmed_by  ? `${plan.admin_confirmed_by} (${fmtShort(plan.admin_confirmed_at)})`   : '-',
          plan?.worker_confirmed_by ? `${plan.worker_confirmed_by} (${fmtShort(plan.worker_confirmed_at)})` : '-',
        ];

        for (let li=0; li<rowCount; li++) {
          const row = ws.getRow(startRow + li);
          if (li===0) {
            baseVals.forEach((v,ci) => { row.getCell(ci+1).value = v; });
            approvalVals.forEach((v,ci) => { row.getCell(12+ci).value = v; });
          }
          if (itemLogs.length === 0) {
            row.getCell(15).value = '-';
            row.getCell(16).value = '-';
            row.getCell(17).value = '수량 변경 없음';
            row.getCell(18).value = 0;
            row.getCell(17).font = { color:{argb:'FF999999'} };
          } else {
            const log  = itemLogs[li];
            const diff = (log.after_qty||0) - (log.before_qty||0);
            row.getCell(15).value = fmtLong(log.updated_at);
            row.getCell(16).value = log.updated_by || '-';
            row.getCell(17).value = `${formatNum(log.before_qty)}개 → ${formatNum(log.after_qty)}개`;
            row.getCell(18).value = diff>0 ? `+${diff}` : `${diff}`;
            row.getCell(17).font = { bold:true, color:{argb:'FF185FA5'} };
            row.getCell(18).font = { bold:true, color:{argb: diff>0?'FF0F6E56':diff<0?'FFDC2626':'FF888888'} };
          }

          for (let c=1; c<=totalCols; c++) {
            const cell = row.getCell(c);
            cell.alignment = { horizontal:'center', vertical:'middle' };
            cell.border = { top:thin, bottom:thin, left:thin, right:thin };
            if (highlight && c<=14) {
              cell.fill = { type:'pattern', pattern:'solid', fgColor:{argb:'FFEAF7F0'} };
            }
          }
          row.getCell(11).font = { bold:true, color:{argb: remain.startsWith('D+') ? 'FFDC2626' : 'FFEA8A00'} };
          row.getCell(8).font  = { bold:true, color:{argb: (s.progress_rate||0) > 0 ? 'FF185FA5' : 'FF888888'} };
        }

        // 서브행이 여러 개면 기본정보/승인정보 열 병합
        if (rowCount > 1) {
          for (let c=1; c<=14; c++) ws.mergeCells(startRow, c, endRow, c);
        }
        r = endRow + 1;
      });

      if (items.length === 0) {
        ws.mergeCells(r,1,r,totalCols);
        const emptyCell = ws.getCell(r,1);
        emptyCell.value = `${year}년 ${month}월에 해당하는 생산현황 데이터가 없습니다.`;
        emptyCell.alignment = { horizontal:'center' };
        emptyCell.font = { color:{argb:'FF999999'} };
      }

      ws.views = [{ state:'frozen', ySplit: headerRowIdx }];

      const buf  = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type:'application/octet-stream' });
      const url  = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const exportFileName = `생산관리_${year}년${pad2(month)}월.xlsx`;
      a.href = url; a.download = exportFileName;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      logDownload('생산관리', exportFileName);

      setShowExportModal(false);
      showMsg('엑셀 파일이 다운로드되었습니다!');
    } catch (err) {
      console.error(err);
      showMsg(err.response?.data?.message || '내보내기 중 오류가 발생했습니다.', 'error');
    } finally {
      setExporting(false);
    }
  };

  const getStatusByPlan = (planId) => activeStatusList.filter(s => s.plan_id === planId);

  // 제품군 탭 필터 — groupTab이 null(전체보기)이면 제품군과 무관하게 전부 보여주고, 특정
  // 제품군을 선택했으면 그 제품군의 계획만 보여줍니다(예전에 등록되어 group_id가 없는
  // 계획은 항상 포함).
  const filteredPlans = plans.filter(p =>
    groupTab==null || p.group_id==null || p.group_id===groupTab
  );

  // 제품군 탭 옆에 표시할 배지용 — 글자 없이 숫자만, 그 제품군의 생산현황 중 "생산중"
  // (생산진행 상태, 완료/보류 제외)인 것만 카운트합니다.
  const activeCountForGroup = (gid) =>
    statusList.filter(s => (gid==null || s.group_id==null || s.group_id===gid) && s.status==='생산진행').length;

  // 생산계획 (생산승인 이전 — 생산계획/관리승인만)
  const planRows    = sortRows(filteredPlans.filter(p => !['생산승인','생산진행','생산완료'].includes(p.progress||'생산계획')), planSortKey, planSortDir);
  // 생산현황 (생산승인 이후 — 생산승인/생산진행/생산완료)
  // 지난달 등 과거월 조회 시엔 해당 월 데이터가 있는 계획만 표시
  const statusPlansRaw = filteredPlans.filter(p => ['생산승인','생산진행','생산완료'].includes(p.progress||'생산계획'));
  const statusPlans    = includeInProgress
    ? statusPlansRaw
    : statusPlansRaw.filter(p => activeStatusList.some(s => s.plan_id === p.id));

  // 요약 카드 데이터
  const totalPlan    = plans.filter(p => p.stage==='계획').length;
  const totalProg    = statusList.filter(s => s.status==='생산진행').length;
  const totalDone    = statusList.filter(s => s.status==='생산완료').length;

  // 진행현황 — 뱃지만 표시 (승인버튼은 모달로 이동)
  const renderProgress = (plan) => {
    const progress = plan.progress || '생산계획';
    return (
      <span style={{ fontSize:12, padding:'4px 10px', borderRadius:20, fontWeight:600,
        background: PROG_BG[progress]||'#f0f0f0', color: PROG_COLOR[progress]||'#555' }}>
        {progress}
      </span>
    );
  };

  return (
    <div id="production-print-root" style={{ padding:24, maxWidth:1690 }}>

      {/* 인쇄 설정: 가로방향, 사이드바/버튼 등 제외, 한 페이지에 맞춤 */}
      <style>{`
        .print-only { display:none; }

        @media print {
          @page { size: A4 landscape; margin: 6mm; }

          /* 사이드바 등 페이지의 다른 요소는 전부 숨기고, 이 컴포넌트 영역만 보이게 */
          html, body, #root { width:100%; height:auto !important; min-height:0 !important; overflow:visible !important; }
          body * {
            visibility: hidden;
            height:auto !important; min-height:0 !important; max-height:none !important;
          }
          /* (빈 페이지 버그 수정) 위 규칙은 visibility/height만 초기화해서, 숨겨진 요소라도
             margin/padding/gap이 남아있으면 그만큼 문서 전체 높이에 계속 더해져 인쇄 영역이
             출력 가능 높이를 살짝 넘기고, 그 넘친 몇 픽셀 때문에 거의 빈 페이지인 2페이지가
             추가로 찍히는 문제가 있었다. 인쇄 대상(#production-print-root)과 그 하위 요소를
             제외한 모든 요소는 margin/padding/gap/border까지 완전히 0으로 만들어 실제로
             차지하는 공간이 전혀 없도록 한다. */
          body *:not(#production-print-root):not(#production-print-root *) {
            margin:0 !important; padding:0 !important; border:0 !important; gap:0 !important;
          }
          #production-print-root, #production-print-root * { visibility: visible; }
          #production-print-root {
            position: absolute !important; top:0 !important; left:0 !important;
            width:100% !important; max-width:none !important;
            margin:0 !important; padding:0 !important;
            /* transform 대신 zoom 사용 — 레이아웃을 실제로 재계산해서 잘림 방지.
               0.56에서 0.52로 살짝 더 축소해서, 인쇄전용 하단 정보(출력일시/출력자)줄이
               차지하는 높이까지 포함해도 한 페이지 출력 가능 영역 안에 여유있게 들어가도록 함
               (기존 0.56은 하단 정보줄 없이 딱 맞게 잡혀있어, 그 줄만큼 초과되며 2페이지가
               생기는 원인이었음). */
            zoom: 0.52;
          }

          .no-print   { display:none !important; }
          .print-only { display:flex !important; }
          body { background:#fff !important; }

          #production-print-root h2 { font-size:16px; margin:0 0 2px !important; }
          #production-print-root p  { font-size:11px; margin:0 !important; }
          #production-print-root table { font-size:11px; width:100% !important; table-layout:auto; }
          #production-print-root th, #production-print-root td { padding:4px 5px !important; white-space:nowrap; }
          #production-print-root > div { margin:0 0 6px 0 !important; page-break-inside: avoid; }
          #production-print-root, #production-print-root * { page-break-after: avoid !important; }
        }
      `}</style>

      {/* 헤더 */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <h2 style={{ fontSize:20, fontWeight:600, margin:0 }}>🏭 생산관리</h2>
          <p style={{ fontSize:13, color:'#888', margin:'4px 0 0' }}>생산계획을 등록하고 생산현황을 관리합니다.</p>
        </div>
        <div className="no-print hdr-actions" style={{ display:'flex', gap:8 }}>
          <button onClick={() => { logPrint('생산관리', '생산관리 화면'); window.print(); }}
            style={{ background:'#475569', color:'#fff', border:'1px solid #ddd', borderRadius:8,
              padding:'10px 16px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
            🖨️ 인쇄
          </button>
          <button onClick={() => setShowExportModal(true)}
            style={{ background:'#16a34a', color:'#fff', border:'none', borderRadius:8,
              padding:'10px 16px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
            📤 내보내기
          </button>
          {canApproveDeleteRequests && (
            <button onClick={() => setShowDeleteRequests(true)}
              style={{ background:'#fff', color:'#dc2626', border:'1px solid #dc2626', borderRadius:8,
                padding:'10px 16px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
              🗑 삭제요청{deleteReqCount > 0 ? ` (${deleteReqCount})` : ''}
            </button>
          )}
          {canRegister && (
            <button onClick={() => { setShowPlanForm(true); setEditPlan(null); }}
              style={{ background:'#185FA5', color:'#fff', border:'none', borderRadius:8,
                padding:'10px 20px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
              + 생산계획 등록
            </button>
          )}
        </div>
      </div>

      {msg && (
        <div className="no-print" style={{ background:msgType==='success'?'#d1fae5':'#fee2e2',
          color:msgType==='success'?'#065f46':'#dc2626',
          padding:'10px 16px', borderRadius:8, marginBottom:16, fontSize:13 }}>{msg}</div>
      )}

      {/* 요약 카드 — 위에서 제품군 탭을 선택하면 이 카드들도 그 제품군 기준으로 다시 집계됩니다. */}
      {(() => {
        // 제품군 탭 필터 (filteredPlans와 동일한 규칙: groupTab이 null이면 전체, 아니면 그
        // 제품군만. group_id가 없는 옛날 생산현황 데이터는 항상 포함)
        const groupStatusList    = statusList.filter(s => groupTab==null || s.group_id==null || s.group_id===groupTab);
        const groupAllStatusList = allStatusList.filter(s => groupTab==null || s.group_id==null || s.group_id===groupTab);

        // 생산계획: 생산승인 전 계획 수량 합계
        const planQty   = planRows.reduce((acc,p) => acc+(p.plan_qty||0), 0);
        // 생산현황: 생산진행 중인 목표수량 합계 (완료 제외)
        const progQty   = groupStatusList.filter(s => s.status!=='생산완료')
                            .reduce((acc,s) => acc+(s.target_qty||0), 0);
        // 생산완료: 실제 생산된 총 수량 (완료 여부와 무관하게, 생산수량 수정 시 즉시 반영)
        const doneQty   = groupAllStatusList.reduce((acc,s) => acc+(s.produced_qty||0), 0);
        // 이번달 누적생산: 완료된 항목은 완료일 기준, 진행중인 항목은 시작일 기준으로
        // 이번달에 해당하면 현재까지의 생산수량을 즉시 합산 (수정 시 바로 반영)
        const now2   = new Date();
        const thisY  = now2.getFullYear();
        const thisM  = now2.getMonth()+1;
        const monthQty = groupAllStatusList
          .filter(s => {
            const refDate = s.status === '생산완료' ? s.completed_at : s.start_date;
            if (!refDate) return false;
            const d = parseDate(refDate); // 'YYYY-MM-DD'
            if (!d) return false;
            const [y, m] = d.split('-').map(Number);
            return y === thisY && m === thisM;
          })
          .reduce((acc,s) => acc+(s.produced_qty||0), 0);
        const now = new Date();
        const monthLabel = now.getFullYear()+'년 '+(now.getMonth()+1)+'월';
        const cards = [
          { label:'생산계획',            value:formatNum(planQty)+'개',  color:'#854F0B', bg:'#FAEEDA' },
          { label:'생산현황',            value:formatNum(progQty)+'개',  color:'#185FA5', bg:'#E6F1FB' },
          { label:'생산완료',            value:formatNum(doneQty)+'개',  color:'#0F6E56', bg:'#E1F5EE' },
          { label:monthLabel+' 누적생산', value:formatNum(monthQty)+'개', color:'#7C3AED', bg:'#EDE9FE' },
        ];
        return (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:24 }}>
            {cards.map(c => (
              <div key={c.label} style={{ background:c.bg, borderRadius:12, padding:'16px 20px',
                border:`1px solid ${c.color}30`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontSize:13, fontWeight:600, color:c.color }}>{c.label}</span>
                <span style={{ fontSize:22, fontWeight:700, color:c.color }}>{c.value}</span>
              </div>
            ))}
          </div>
        );
      })()}

      {/* 제품군 탭 — 재고관리에서 등록한 제품군(안전벨트/안전블럭 등)을 예전 전체/자체생산/
          주문생산 탭과 완전히 같은 스타일로 표시합니다. 옆의 배지는 글자 없이 숫자만 — 그
          제품군의 생산현황 중 "생산중"(생산진행 상태)인 건수입니다. 선택하면 아래 목록뿐
          아니라 위 요약 카드(생산계획/생산현황/생산완료/이번달 누적생산)도 이 제품군 기준으로
          다시 집계됩니다. 생산계획 등록 시 모델명도 여기서 선택된(또는 폼에서 다시 고른)
          제품군에 재고관리로 등록된 완제품 목록을 그대로 가져와 사용합니다.
          제품군이 1개뿐이면 위쪽 useEffect가 이미 groupTab을 그 제품군으로 고정해두므로,
          고를 필요가 없는 탭 자체를 숨긴다(생산전광판의 1개일 때 하위메뉴 숨김과 동일한 규칙). */}
      {groups.length > 1 && (
        <div className="no-print" style={{ display:'flex', gap:0, marginBottom:20, borderBottom:'2px solid #e5e7eb' }}>
          <button onClick={() => setGroupTab(null)}
            style={{ padding:'10px 24px', border:'none', cursor:'pointer', fontSize:14, fontWeight:600,
              borderBottom: groupTab==null?'2px solid #185FA5':'2px solid transparent',
              background:'none', color:groupTab==null?'#185FA5':'#888', marginBottom:-2 }}>
            전체
            <span style={{ fontSize:11, marginLeft:6, background:groupTab==null?'#185FA5':'#e5e7eb',
              color:groupTab==null?'#fff':'#888', padding:'1px 7px', borderRadius:20 }}>
              {activeCountForGroup(null)}
            </span>
          </button>
          {groups.map(g => (
            <button key={g.id} onClick={() => setGroupTab(g.id)}
              style={{ padding:'10px 24px', border:'none', cursor:'pointer', fontSize:14, fontWeight:600,
                borderBottom: groupTab===g.id?'2px solid #185FA5':'2px solid transparent',
                background:'none', color:groupTab===g.id?'#185FA5':'#888', marginBottom:-2 }}>
              {g.name}
              <span style={{ fontSize:11, marginLeft:6, background:groupTab===g.id?'#185FA5':'#e5e7eb',
                color:groupTab===g.id?'#fff':'#888', padding:'1px 7px', borderRadius:20 }}>
                {activeCountForGroup(g.id)}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* ══════════════════
           생산계획
      ══════════════════ */}
      {canSeePlanList && (
      <div style={{ marginBottom:32 }}>
        <div style={{ fontSize:16, fontWeight:700, color:'#1a1a1a', marginBottom:10,
          paddingBottom:8, borderBottom:'2px solid #185FA5', display:'flex', alignItems:'center', gap:8 }}>
          📋 생산계획
          <span style={{ fontSize:12, color:'#888', fontWeight:400 }}>
            (생산승인 전 단계)
          </span>
        </div>

        <div style={{ background:'#fff', borderRadius:12, border:'1px solid #e5e7eb',
          overflowX:'auto', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr>
                <SortableTh label="구 분" sortKey="category" activeKey={planSortKey} dir={planSortDir} onSort={togglePlanSort} style={{ ...cellHead, width:80 }} />
                <SortableTh label="단 계" sortKey="stage" activeKey={planSortKey} dir={planSortDir} onSort={togglePlanSort} style={{ ...cellHead, width:70 }} />
                <SortableTh label="진행현황" sortKey="progress" activeKey={planSortKey} dir={planSortDir} onSort={togglePlanSort} style={{ ...cellHead, width:100 }} />
                <SortableTh label="관리담당자" sortKey="manager" activeKey={planSortKey} dir={planSortDir} onSort={togglePlanSort} style={{ ...cellHead, width:90 }} />
                <SortableTh label="모 델 명" sortKey="model_no" activeKey={planSortKey} dir={planSortDir} onSort={togglePlanSort} style={{ ...cellHead, width:120 }} />
                <SortableTh label="옵 션" sortKey="colors" activeKey={planSortKey} dir={planSortDir} onSort={togglePlanSort} style={{ ...cellHead, width:90 }} />
                <SortableTh label="계획수량" sortKey="plan_qty" activeKey={planSortKey} dir={planSortDir} onSort={togglePlanSort} style={{ ...cellHead, width:75 }} />
                <SortableTh label="시 작 일" sortKey="start_date" activeKey={planSortKey} dir={planSortDir} onSort={togglePlanSort} style={{ ...cellHead, width:95 }} />
                <SortableTh label="목 표 일" sortKey="end_date" activeKey={planSortKey} dir={planSortDir} onSort={togglePlanSort} style={{ ...cellHead, width:95 }} />
                <th style={{ ...cellHead, width:130 }}>승 인</th>
                <th style={{ ...cellHead }}>비 고</th>
                <th style={{ ...cellHead, width:60 }}>상세</th>
              </tr>
            </thead>
            <tbody>
              {planRows.length === 0 ? (
                <tr>
                  <td colSpan={12} style={{ ...cellBody, padding:'40px', color:'#aaa', fontSize:15 }}>
                    생산계획이 없습니다.
                  </td>
                </tr>
              ) : planRows.map((plan, i) => {
                const models     = parseJSON(plan.model_no);
                const colors     = parseJSON(plan.colors);
                const isSelected = actionPlan?.id === plan.id;

                return (
                  <tr key={plan.id} style={{ background:i%2===0?'#fff':'#fafafa' }}>

                    {/* 구분 — 클릭하면 모달창 (관리자만) */}
                    <td style={{ ...cellBody, cursor: canEdit?'pointer':'default' }}
                      onClick={() => canEdit && setActionPlan(plan)}>
                      <span style={{ fontSize:14, fontWeight:700,
                        color: plan.category==='자체생산'?'#185FA5':'#854F0B',
                        textDecoration: canEdit?'underline':'none',
                        textUnderlineOffset:3 }}>
                        {plan.category}
                      </span>
                    </td>

                    {/* 단계 */}
                    <td style={cellBody}>
                      <span style={{ fontSize:13, padding:'4px 10px', borderRadius:20, fontWeight:600,
                        background: STAGE_BG[plan.stage||'계획'],
                        color:      STAGE_COLOR[plan.stage||'계획'] }}>
                        {plan.stage || '계획'}
                      </span>
                    </td>

                    {/* 진행현황 */}
                    <td style={{ ...cellBody, minWidth:130 }}>{renderProgress(plan)}</td>

                    {/* 관리담당자 - 단계별 승인자 이름만 표시 */}
                    <td style={{ ...cellBody, fontWeight:600, fontSize:14 }}>
                      {plan.progress === '생산계획' && (
                        <span>{plan.manager||'-'}</span>
                      )}
                      {plan.progress === '관리승인' && (
                        <span style={{ color:'#185FA5' }}>{plan.admin_confirmed_by||plan.manager||'-'}</span>
                      )}
                      {['생산승인','생산진행','생산완료'].includes(plan.progress) && (
                        <span style={{ color:'#0F6E56' }}>{plan.worker_confirmed_by||plan.manager||'-'}</span>
                      )}
                    </td>

                    {/* 모델명 줄바꿈 - 가운데 정렬 */}
                    <td style={{ ...cellBody, textAlign:'center' }}>
                      {models.map(m => (
                        <div key={m} style={{ fontSize:14, fontWeight:500, lineHeight:1.9 }}>{m}</div>
                      ))}
                    </td>

                    {/* 색상 줄바꿈 */}
                    <td style={cellBody}>
                      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:3 }}>
                        {colors.map(c => (
                          <span key={c} style={{ fontSize:13, padding:'3px 10px', borderRadius:10,
                            fontWeight:600, background:COLOR_STYLE[c]?.bg||'#ddd',
                            color:COLOR_STYLE[c]?.color||'#fff' }}>{c}</span>
                        ))}
                      </div>
                    </td>

                    {/* 계획수량 */}
                    <td style={{ ...cellBody, fontWeight:700, fontSize:16, color:'#185FA5' }}>
                      {formatNum(plan.plan_qty)}
                    </td>

                    {/* 날짜 */}
                    <td style={{ ...cellBody, fontSize:13 }}>{plan.start_date||'-'}</td>
                    <td style={{ ...cellBody, fontSize:13 }}>{plan.end_date||'-'}</td>

                    {/* 승인 탭 — 버튼만 표시, 이력은 비고에 */}
                    <td style={{ ...cellBody, textAlign:'left', paddingLeft:8, fontSize:13 }}>
                      {/* 관리승인 버튼 (생산계획 단계, 관리승인 실행권한(admin_approval_action)이
                          있어야만 노출 — 별도의 "표시" 권한 레이어는 폐지됨, 관리자는 항상 통과) */}
                      {canSeeAdminApproval && (plan.progress||'생산계획')==='생산계획' && (
                        <button onClick={() => handleProgress(plan, '관리승인')}
                          style={{ fontSize:12, padding:'4px 8px', border:'1px solid #854F0B',
                            borderRadius:6, background:'#FAEEDA', color:'#854F0B',
                            cursor:'pointer', fontWeight:600, display:'block', width:'100%' }}>
                          ✓ 관리승인
                        </button>
                      )}
                      {/* 생산승인 버튼 (관리승인 단계, 생산승인 실행권한(production_approval_action)이
                          있어야만 노출 — 별도의 "표시" 권한 레이어는 폐지됨, 관리자는 항상 통과) */}
                      {canSeeWorkerApproval && (plan.progress||'생산계획')==='관리승인' && (
                        <button onClick={() => handleProgress(plan, '생산승인')}
                          style={{ fontSize:12, padding:'4px 8px', border:'1px solid #0F6E56',
                            borderRadius:6, background:'#E1F5EE', color:'#0F6E56',
                            cursor:'pointer', fontWeight:600, display:'block', width:'100%' }}>
                          ✓ 생산승인
                        </button>
                      )}
                      {/* 승인 완료 후: 생산승인 완료 표시만 */}
                      {(plan.progress||'생산계획')==='생산승인' && (
                        <span style={{ fontSize:12, padding:'3px 8px', borderRadius:20,
                          background:'#E1F5EE', color:'#0F6E56', fontWeight:600 }}>
                          ✅ 생산승인 완료
                        </span>
                      )}
                      {/* 둘다 없으면 */}
                      {!canSeeAdminApproval && !canSeeWorkerApproval && !['관리승인','생산승인'].includes(plan.progress||'생산계획') && (
                        <span style={{ color:'#ccc', fontSize:12 }}>-</span>
                      )}
                    </td>

                    {/* 비고 — 가장 최신 업데이트 사항(등록/관리승인/생산승인/보류 등 마지막 한 줄)만 표시 */}
                    <td style={{ ...cellBody, textAlign:'left', paddingLeft:8, color:'#555', fontSize:12, minWidth:180 }}>
                      {getLatestNoteLine(plan.note)}
                    </td>

                    {/* 상세 — "생산계획 상세보기"(plan_detail_visible) 권한이 있어야만 노출.
                        생산현황 목록의 상세보기(status_detail_visible)와는 별개의 진입점이지만,
                        같은 상세 모달(StatusDetail/selectedPlan·showDetail)을 그대로 연다. */}
                    <td style={cellBody}>
                      {canSeePlanDetail && (
                        <button onClick={() => { setSelectedPlan(plan); setShowDetail(true); }}
                          style={{ background:'none', border:'1px solid #185FA5', borderRadius:6,
                            padding:'4px 8px', fontSize:13, color:'#185FA5', cursor:'pointer' }}>
                          상세
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* ══════════════════
           생산현황
      ══════════════════ */}
      {canSeeStatusList && (
      <div>
        <div style={{ fontSize:16, fontWeight:700, color:'#1a1a1a', marginBottom:10,
          paddingBottom:8, borderBottom:'2px solid #0F6E56',
          display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
          <span style={{ display:'flex', alignItems:'center', gap:8 }}>
            🏭 생산현황
            <span style={{ fontSize:12, color:'#888', fontWeight:400 }}>
              {includeInProgress ? '(생산승인 이후)' : '(과거 완료 이력)'}
            </span>
          </span>

          {/* 월 이동 + 전체목록 토글 */}
          <span className="no-print" ref={datePickerRef} style={{ display:'flex', alignItems:'center', gap:8, position:'relative' }}>
            <button onClick={goPrevMonth} disabled={!viewMonth}
              style={{ background:'#fff', border:'1px solid #ddd', borderRadius:6, width:28, height:28,
                fontSize:13, cursor:!viewMonth?'not-allowed':'pointer', color:!viewMonth?'#ccc':'#555' }}>◀</button>
            <span onClick={() => setShowDatePicker(v=>!v)}
              style={{ fontSize:16, fontWeight:700, color:'#000', minWidth:100, textAlign:'center', cursor:'pointer' }}>
              {viewYear}년{viewMonth ? ` ${viewMonth}월` : ''}
            </span>
            <button onClick={goNextMonth} disabled={isCurrentMonthView || !viewMonth}
              style={{ background:'#fff', border:'1px solid #ddd', borderRadius:6, width:28, height:28,
                fontSize:13, cursor:(isCurrentMonthView||!viewMonth)?'not-allowed':'pointer',
                color:(isCurrentMonthView||!viewMonth)?'#ccc':'#555' }}>▶</button>
            {!isCurrentMonthView && (
              <button onClick={goThisMonth}
                style={{ fontSize:12, padding:'5px 10px', background:'#E6F1FB', color:'#185FA5',
                  border:'1px solid #185FA5', borderRadius:6, cursor:'pointer', fontWeight:600 }}>
                이번달
              </button>
            )}
            <button onClick={() => setShowAllConfirmed(v => !v)}
              style={{ fontSize:12, padding:'5px 10px',
                background: showAllConfirmed ? '#0F6E56' : '#fff',
                color:      showAllConfirmed ? '#fff'    : '#0F6E56',
                border:'1px solid #0F6E56', borderRadius:6, cursor:'pointer', fontWeight:600 }}>
              📋 목록{showAllConfirmed ? ' (전체 표시중)' : ''}
            </button>

            {showDatePicker && (
              <div style={{ position:'absolute', top:34, left:36, background:'#fff', border:'1px solid #ddd',
                borderRadius:10, boxShadow:'0 4px 16px rgba(0,0,0,0.12)', padding:12, zIndex:50, width:220 }}
                onClick={e => e.stopPropagation()}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                  <button onClick={() => setPickerYear(y=>y-1)}
                    style={{ background:'none', border:'none', fontSize:13, color:'#888', cursor:'pointer', padding:'2px 4px' }}>◀</button>
                  <span style={{ fontWeight:600, fontSize:14 }}>{pickerYear}년</span>
                  <button onClick={() => setPickerYear(y=>y+1)}
                    style={{ background:'none', border:'none', fontSize:13, color:'#888', cursor:'pointer', padding:'2px 4px' }}>▶</button>
                </div>
                <button
                  onClick={() => { setViewYear(pickerYear); setViewMonth(null); setShowDatePicker(false); }}
                  style={{ width:'100%', padding:'6px 0', marginBottom:6, borderRadius:6, border:'1px solid',
                    borderColor: (pickerYear===viewYear && !viewMonth) ? '#185FA5' : '#ddd',
                    background:  (pickerYear===viewYear && !viewMonth) ? '#E6F1FB' : '#fff',
                    color:       (pickerYear===viewYear && !viewMonth) ? '#185FA5' : '#555',
                    fontSize:13, fontWeight:600, cursor:'pointer' }}>
                  {pickerYear}년 전체보기
                </button>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:6 }}>
                  {Array.from({length:12}, (_,i)=>i+1).map(m => {
                    const active = pickerYear===viewYear && m===viewMonth;
                    return (
                      <button key={m}
                        onClick={() => { setViewYear(pickerYear); setViewMonth(m); setShowDatePicker(false); }}
                        style={{ padding:'6px 0', borderRadius:6, border:'1px solid',
                          borderColor: active?'#185FA5':'#ddd',
                          background:  active?'#E6F1FB':'#fff',
                          color:       active?'#185FA5':'#555',
                          fontSize:14, cursor:'pointer' }}>
                        {m}월
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </span>
        </div>

        {/* 자체생산 섹션 */}
        {['자체생산','주문생산'].map(cat => {
          const catStatusPlans = statusPlans.filter(p => p.category===cat);

          return (
            <div key={cat} style={{ marginBottom:24 }}>
              <div style={{ fontSize:14, fontWeight:600, color: cat==='자체생산'?'#185FA5':'#854F0B',
                marginBottom:8, paddingLeft:4, borderLeft:`3px solid ${cat==='자체생산'?'#185FA5':'#854F0B'}`,
                paddingLeft:10 }}>
                📌 {cat}
              </div>

              <div style={{ background:'#fff', borderRadius:12, border:'1px solid #e5e7eb',
                overflowX:'auto', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr>
                      <SortableTh label="구 분" sortKey="category" activeKey={statusSortKey} dir={statusSortDir} onSort={toggleStatusSort} style={{ ...cellHead, width:90 }} />
                      <SortableTh label="진행현황" sortKey="status" activeKey={statusSortKey} dir={statusSortDir} onSort={toggleStatusSort} style={{ ...cellHead, width:100 }} />
                      <SortableTh label="담 당 자" sortKey="manager" activeKey={statusSortKey} dir={statusSortDir} onSort={toggleStatusSort} style={{ ...cellHead, width:90 }} />
                      <SortableTh label="모 델 명" sortKey="model_no" activeKey={statusSortKey} dir={statusSortDir} onSort={toggleStatusSort} style={{ ...cellHead, width:120 }} />
                      <SortableTh label="옵 션" sortKey="color" activeKey={statusSortKey} dir={statusSortDir} onSort={toggleStatusSort} style={{ ...cellHead, width:70 }} />
                      <SortableTh label="목표수량" sortKey="target_qty" activeKey={statusSortKey} dir={statusSortDir} onSort={toggleStatusSort} style={{ ...cellHead, width:80 }} />
                      <SortableTh label="생산수량" sortKey="produced_qty" activeKey={statusSortKey} dir={statusSortDir} onSort={toggleStatusSort} style={{ ...cellHead, width:80 }} />
                      <SortableTh label="공 정 률" sortKey="progress_rate" activeKey={statusSortKey} dir={statusSortDir} onSort={toggleStatusSort} style={{ ...cellHead, width:100 }} />
                      <SortableTh label="시 작 일" sortKey="start_date" activeKey={statusSortKey} dir={statusSortDir} onSort={toggleStatusSort} style={{ ...cellHead, width:100 }} />
                      <SortableTh label="목 표 일" sortKey="end_date" activeKey={statusSortKey} dir={statusSortDir} onSort={toggleStatusSort} style={{ ...cellHead, width:100 }} />
                      <SortableTh label="완 료 일" sortKey="completed_at" activeKey={statusSortKey} dir={statusSortDir} onSort={toggleStatusSort} style={{ ...cellHead, width:120 }} />
                      <th style={{ ...cellHead, width:50 }}>상세</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      // 목록 컬럼명 클릭 정렬은 화면에 실제로 표시되는 값(생산현황 개별 항목, s)
                      // 기준으로 동작해야 하므로, plan 단위가 아니라 plan×생산현황 항목을 평탄화한
                      // 뒤 그 항목(s) 필드로 정렬한다 — plan 하나에 사양(옵션)별 생산현황이
                      // 여러 건 딸린 구조라 plan만 정렬하면 s 필드 기준 정렬이 맞지 않는다.
                      const flatRows = catStatusPlans.flatMap(plan => getStatusByPlan(plan.id).map(s => ({ s, plan })));
                      const sortedRows = sortRows(flatRows.map(r => r.s), statusSortKey, statusSortDir)
                        .map(s => ({ s, plan: flatRows.find(r => r.s.id === s.id).plan }));
                      return sortedRows.length === 0 ? (
                        <tr>
                          <td colSpan={12} style={{ ...cellBody, padding:'30px', color:'#aaa', fontSize:13 }}>
                            {cat} 생산현황이 없습니다.
                          </td>
                        </tr>
                      ) : sortedRows.map(({ s, plan }, i) => (
                        <StatusRow
                          key={s.id}
                          status={s}
                          plan={plan}
                          isAdmin={canEdit}
                          isWorker={false}
                          idx={i}
                          onHold={() => setShowHoldModal({ plan, status: s })}
                          onUpdate={() => { loadPlans(); loadStatus(); }}
                          onDetail={() => { setSelectedPlan(plan); setShowDetail(true); }}
                          showMsg={showMsg}
                        />
                      ));
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
      )}

      {/* 생산계획 폼 */}
      {showPlanForm && (
        <PlanForm
          editData={editPlan}
          groupId={groupTab ?? groups[0]?.id ?? null}
          groups={groups}
          onClose={() => { setShowPlanForm(false); setEditPlan(null); }}
          onSaved={() => {
            setShowPlanForm(false); setEditPlan(null);
            // 생산계획을 새로 등록/수정하면 production_status(생산현황) 행도 함께 바뀌므로
            // (신규 등록 시 아직 승인 전이라 생산현황 행이 없을 수도 있지만, 수정 시에는
            // syncStatusRows가 이미 생산현황 행까지 갱신해둔 상태) 두 목록을 모두 새로고침해야
            // 생산현황 상세/목록이 새로고침 없이 즉시 반영됩니다.
            loadPlans();
            loadStatus();
            showMsg(editPlan?'수정 완료!':'등록 완료!');
          }}
        />
      )}

      {/* 상세 모달 */}
      {showDetail && selectedPlan && (
        <StatusDetail
          plan={selectedPlan}
          statusList={getStatusByPlan(selectedPlan.id)}
          isAdmin={canEdit}
          isSuperAdmin={isSuperAdmin}
          user={user}
          onClose={() => { setShowDetail(false); setSelectedPlan(null); }}
          onConfirmDone={() => { setShowDetail(false); setSelectedPlan(null); loadPlans(); loadStatus(); }}
          onUpdate={() => { loadPlans(); loadStatus(); }}
          onDeleted={() => { setShowDetail(false); setSelectedPlan(null); loadPlans(); loadStatus(); loadDeleteReqCount(); }}
          onEdit={p => { setShowDetail(false); setSelectedPlan(null); setEditPlan(p); setShowPlanForm(true); }}
          showMsg={showMsg}
        />
      )}

      {/* 삭제요청 목록 모달 (최고관리자) */}
      {showDeleteRequests && (
        <DeleteRequestsModal
          onClose={() => setShowDeleteRequests(false)}
          onChanged={() => { loadPlans(); loadStatus(); loadDeleteReqCount(); }}
          showMsg={showMsg}
        />
      )}

      {/* 생산계획 수정/삭제/승인 모달 */}
      {actionPlan && (
        <PlanActionModal
          plan={actionPlan}
          isAdmin={canEdit}
          isSuperAdmin={isSuperAdmin}
          onClose={() => setActionPlan(null)}
          onEdit={p => { setEditPlan(p); setShowPlanForm(true); }}
          onDelete={deletePlan}
        />
      )}

      {/* 보류 사유 모달 */}
      {showHoldModal && (
        <HoldModal
          info={showHoldModal}
          onClose={() => setShowHoldModal(null)}
          onSaved={(note) => {
            handleProgress(showHoldModal.plan, '생산보류', note);
            setShowHoldModal(null);
          }}
        />
      )}

      {/* 인쇄 전용 하단 정보: 좌측 출력일시 / 우측 출력자 */}
      <div className="print-only" style={{ display:'none', justifyContent:'space-between',
        marginTop:14, paddingTop:8, borderTop:'1px solid #ddd', fontSize:12, color:'#555' }}>
        <span>출력일시: {(() => {
          const d = new Date();
          return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
        })()}</span>
        <span>출력자: {printedByLabel(user)}</span>
      </div>

      {/* 내보내기(엑셀) 모달 */}
      {showExportModal && (
        <ExportModal
          defaultYear={viewYear}
          defaultMonth={viewMonth || (nowDate.getMonth()+1)}
          exporting={exporting}
          onClose={() => setShowExportModal(false)}
          onExport={handleExport}
        />
      )}
    </div>
  );
}

// ── 내보내기(월 선택) 모달 ─────────────────────────
function ExportModal({ defaultYear, defaultMonth, exporting, onClose, onExport }) {
  const [year, setYear]   = useState(defaultYear);
  const [month, setMonth] = useState(defaultMonth);
  const years  = Array.from({length:5}, (_,i) => defaultYear - i);
  const months = Array.from({length:12}, (_,i) => i+1);

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:340, maxWidth:'92vw' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <h3 style={{ fontSize:16, fontWeight:600, margin:0 }}>📤 엑셀 내보내기</h3>
          <button onClick={onClose}
            style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        <p style={{ fontSize:13, color:'#888', marginBottom:16 }}>
          내보낼 월을 선택하세요. 선택한 월의 생산계획 / 승인내역 / 업데이트내역이 엑셀 파일로 저장됩니다.
        </p>

        <div style={{ display:'flex', gap:8, marginBottom:20 }}>
          <select value={year} onChange={e => setYear(Number(e.target.value))}
            style={{ flex:1, padding:'10px', borderRadius:8, border:'1px solid #ddd', fontSize:14 }}>
            {years.map(y => <option key={y} value={y}>{y}년</option>)}
          </select>
          <select value={month} onChange={e => setMonth(Number(e.target.value))}
            style={{ flex:1, padding:'10px', borderRadius:8, border:'1px solid #ddd', fontSize:14 }}>
            {months.map(m => <option key={m} value={m}>{m}월</option>)}
          </select>
        </div>

        <button onClick={() => onExport(year, month)} disabled={exporting}
          style={{ width:'100%', padding:'12px', background:exporting?'#aaa':'#0F6E56', color:'#fff',
            border:'none', borderRadius:10, fontSize:14, fontWeight:700,
            cursor:exporting?'not-allowed':'pointer' }}>
          {exporting ? '내보내는 중...' : '엑셀 다운로드'}
        </button>
      </div>
    </div>
  );
}

// ── 생산현황 행 ────────────────────────────────────
function StatusRow({ status: s, plan, isAdmin, isWorker, idx, onHold, onUpdate, onDetail, showMsg }) {
  const { hasDetail } = usePermissions();
  const canSeeDetail = hasDetail('productions', 'status_detail_visible');
  const [showQtyModal, setShowQtyModal] = useState(false);
  const canUpdate = isAdmin || isWorker;

  return (
    <>
      <tr style={{ background: idx%2===0?'#fff':'#fafafa' }}>
        <td style={cellBody}>
          <span style={{ fontSize:14, padding:'3px 8px', borderRadius:10, fontWeight:500,
            background: s.category==='자체생산'?'#E6F1FB':'#FAEEDA',
            color:      s.category==='자체생산'?'#185FA5':'#854F0B' }}>
            {s.category}
          </span>
        </td>
        <td style={cellBody}>
          <span style={{ fontSize:14, padding:'3px 10px', borderRadius:20, fontWeight:600,
            background: s.status==='생산완료'?'#E1F5EE':'#E6F1FB',
            color:      s.status==='생산완료'?'#0F6E56':'#185FA5' }}>
            {s.status}
          </span>
        </td>
        <td style={{ ...cellBody, fontSize:14 }}>{s.manager||'-'}</td>
        <td style={{ ...cellBody, fontWeight:600, fontSize:14 }}>{s.model_no}</td>
        <td style={cellBody}>
          <span style={{ fontSize:13, padding:'3px 10px', borderRadius:10, fontWeight:600,
            background:COLOR_STYLE[s.color]?.bg||'#ddd', color:COLOR_STYLE[s.color]?.color||'#fff' }}>
            {s.color}
          </span>
        </td>
        <td style={{ ...cellBody, fontWeight:600, fontSize:14 }}>{formatNum(s.target_qty)}</td>

        {/* 생산수량 — 클릭하면 수정 팝업 */}
        <td style={{ ...cellBody, fontWeight:700, fontSize:14,
          color: s.status==='생산완료'?'#0F6E56':'#185FA5',
          cursor: canUpdate && s.status!=='생산완료'?'pointer':'default' }}
          onClick={() => canUpdate && s.status!=='생산완료' && setShowQtyModal(true)}>
          {formatNum(s.produced_qty)}
          {canUpdate && s.status!=='생산완료' && (
            <div style={{ fontSize:11, color:'#aaa', fontWeight:400 }}>클릭수정</div>
          )}
        </td>

        <td style={cellBody}>
          <div style={{ fontSize:14, fontWeight:700, color:s.progress_rate>=100?'#0F6E56':'#185FA5', marginBottom:2 }}>
            {s.progress_rate}%
          </div>
          <div style={{ background:'#e5e7eb', borderRadius:4, height:6, overflow:'hidden' }}>
            <div style={{ background:s.progress_rate>=100?'#0F6E56':'#185FA5',
              width:Math.min(s.progress_rate,100)+'%', height:6, borderRadius:4 }} />
          </div>
        </td>
        <td style={{ ...cellBody, fontSize:14 }}>{s.start_date||'-'}</td>
        <td style={{ ...cellBody, fontSize:14 }}>{s.end_date||'-'}</td>

        {/* 완료일: 진행중=목표일 기준 D-Day, 완료=실제완료일+차이 */}
        <td style={{ ...cellBody, textAlign:'center', minWidth:110 }}>
          <CompletedDateCell
            startDate={s.start_date}
            endDate={s.end_date}
            completedAt={s.completed_at}
          />
        </td>

        {/* 상세 */}
        <td style={cellBody}>
          {canSeeDetail && (
            <button onClick={onDetail}
              style={{ background:'none', border:'1px solid #185FA5', borderRadius:6,
                padding:'4px 8px', fontSize:13, color:'#185FA5', cursor:'pointer' }}>
              상세
            </button>
          )}
        </td>
      </tr>

      {/* 생산수량 수정 팝업 */}
      {showQtyModal && (
        <QtyEditModal
          status={s}
          isAdmin={canUpdate}
          onClose={() => setShowQtyModal(false)}
          onSaved={() => { setShowQtyModal(false); onUpdate(); }}
          onHold={onHold}
          showMsg={showMsg}
        />
      )}
    </>
  );
}

// ── 생산수량 수정 팝업 ────────────────────────────
function QtyEditModal({ status: s, isAdmin, onClose, onSaved, onHold, showMsg }) {
  const [addQty, setAddQty]   = useState('');
  const [loading, setLoading] = useState(false);

  const currentQty = s.produced_qty || 0;
  const newTotal   = currentQty + (Number(addQty) || 0);
  const newRate    = s.target_qty > 0 ? Math.min(Math.round((newTotal / s.target_qty) * 100), 100) : 0;

  const handleUpdate = async () => {
    if (!addQty || Number(addQty) === 0) return showMsg('추가할 수량을 입력해주세요.', 'error');
    setLoading(true);
    try {
      const res = await API.patch('/productions/status/'+s.id+'/qty', { produced_qty: newTotal });
      showMsg('생산수량 업데이트 완료! 공정률: '+res.data.progress_rate+'%');
      // 생산완료 자동처리 (백엔드에서 처리)
      onSaved();
    } catch(err) { showMsg(err.response?.data?.message||'오류','error'); }
    finally { setLoading(false); }
  };

  const handleComplete = async () => {
    if (!window.confirm('생산완료 처리 시 재고현황이 자동 업데이트됩니다. 진행하시겠습니까?')) return;
    try {
      const res = await API.patch('/productions/status/'+s.id+'/complete');
      showMsg(res.data.message); onSaved();
    } catch(err) { showMsg(err.response?.data?.message||'오류','error'); }
  };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:360, maxWidth:'92vw' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <h3 style={{ fontSize:16, fontWeight:600, margin:0 }}>✏️ 생산수량 수정</h3>
          <button onClick={onClose}
            style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        {/* 현황 정보 */}
        <div style={{ background:'#f8fafc', borderRadius:8, padding:'12px 14px', marginBottom:16 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
            <span style={{ fontWeight:700, fontSize:14 }}>{s.model_no}</span>
            <span style={{ padding:'2px 8px', borderRadius:10, fontWeight:600, fontSize:12,
              background:COLOR_STYLE[s.color]?.bg||'#ddd', color:COLOR_STYLE[s.color]?.color||'#fff' }}>
              {s.color}
            </span>
          </div>
          <div style={{ fontSize:13, color:'#666' }}>
            목표수량: <strong>{formatNum(s.target_qty)}</strong> |
            공정률: <strong>{s.progress_rate}%</strong>
          </div>
        </div>

        {/* 누적수량 (읽기전용 표기) */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
          background:'#EEF6FF', borderRadius:8, padding:'10px 14px', marginBottom:16 }}>
          <span style={{ fontSize:13, color:'#185FA5', fontWeight:600 }}>누적 생산수량</span>
          <span style={{ fontSize:20, fontWeight:700, color:'#185FA5' }}>
            {formatNum(currentQty)}
            {Number(addQty) !== 0 && !isNaN(Number(addQty)) && (
              <span style={{ fontSize:14, color:'#0F6E56', fontWeight:600 }}> → {formatNum(newTotal)} ({newRate}%)</span>
            )}
          </span>
        </div>

        {/* 추가수량 입력 */}
        <label style={{ fontSize:13, color:'#555', fontWeight:500, display:'block', marginBottom:6 }}>
          이번에 추가할 수량
        </label>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:16 }}>
          <button onClick={() => setAddQty(q => String(Math.max(0, (Number(q)||0)-1)))}
            style={{ width:36, height:36, border:'1px solid #ddd', borderRadius:8,
              background:'#f0f0f0', fontSize:18, cursor:'pointer' }}>-</button>
          <input type="number" value={addQty} onChange={e => setAddQty(e.target.value)}
            placeholder="0"
            style={{ flex:1, padding:'9px 12px', border:'1px solid #ddd', borderRadius:8,
              fontSize:18, textAlign:'center', fontWeight:700, outline:'none' }} />
          <button onClick={() => setAddQty(q => String((Number(q)||0)+1))}
            style={{ width:36, height:36, border:'1px solid #ddd', borderRadius:8,
              background:'#f0f0f0', fontSize:18, cursor:'pointer' }}>+</button>
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          <button onClick={handleUpdate} disabled={loading}
            style={{ width:'100%', padding:'11px', background:loading?'#aaa':'#185FA5', color:'#fff',
              border:'none', borderRadius:8, fontSize:14, fontWeight:600,
              cursor:loading?'not-allowed':'pointer' }}>
            {loading ? '저장 중...' : '수량 업데이트'}
          </button>
          <button onClick={onClose}
            style={{ width:'100%', padding:'10px', background:'none', border:'1px solid #ddd',
              borderRadius:8, fontSize:14, fontWeight:400, cursor:'pointer', color:'#888' }}>
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}


// ── 생산계획 수정/삭제/승인 모달 ──────────────────────
// ── 삭제요청 목록 모달 (최고관리자 또는 "생산현황 삭제" 권한자) ──────────────────
// 항목을 클릭하면 생산현황 상세(StatusDetail)를 이 목록 위에 겹쳐서 열어 전체 내용을 확인할 수
// 있고, 그 상세 화면 상단에도 삭제승인/거부 버튼이 함께 뜬다(approveRequest prop). 상세 모달의
// 오버레이가 이 목록 모달보다 z-index가 높기 때문에, 상세 바깥을 클릭해 닫아도 상세만 사라지고
// 이 목록은 그대로 남는다.
function DeleteRequestsModal({ onClose, onChanged, showMsg }) {
  const { canWrite: canWriteMenu } = usePermissions();
  const isSuperAdmin = (['superadmin','admin'].includes(JSON.parse(sessionStorage.getItem('user') || '{}').role));
  const isAdminView  = isSuperAdmin || canWriteMenu('productions');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [viewing, setViewing]   = useState(null); // 클릭한 요청(생산현황 상세로 열기)
  const [viewStatuses, setViewStatuses] = useState([]);

  const load = () => {
    setLoading(true);
    API.get('/productions/delete-requests')
      .then(r => { setRequests(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const handleApprove = async (id) => {
    if (!window.confirm('승인하시겠습니까? 생산계획과 생산현황이 모두 삭제됩니다. 되돌릴 수 없습니다.')) return;
    try {
      await API.patch('/productions/delete-requests/'+id+'/approve');
      showMsg('승인 완료! 삭제되었습니다.');
      setViewing(null);
      load(); onChanged();
    } catch (err) {
      showMsg(err.response?.data?.message || '오류가 발생했습니다.', 'error');
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('이 삭제 요청을 거부하시겠습니까?')) return;
    try {
      await API.patch('/productions/delete-requests/'+id+'/reject');
      showMsg('삭제 요청을 거부했습니다.');
      setViewing(null);
      load(); onChanged();
    } catch (err) {
      showMsg(err.response?.data?.message || '오류가 발생했습니다.', 'error');
    }
  };

  // 리스트 항목 클릭 → 해당 생산계획의 생산현황(사양별) 전체를 불러와 StatusDetail로 연다.
  const openDetail = async (r) => {
    setViewing(r);
    try {
      const res = await API.get('/productions/status', { params: { plan_id: r.plan_id } });
      setViewStatuses(res.data || []);
    } catch {
      setViewStatuses([]);
    }
  };

  const user = JSON.parse(sessionStorage.getItem('user') || '{}');

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:560, maxWidth:'92vw', maxHeight:'80vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <h3 style={{ fontSize:17, fontWeight:600, margin:0 }}>🗑 생산현황 삭제요청</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        {loading ? (
          <div style={{ textAlign:'center', padding:'30px 0', color:'#aaa' }}>불러오는 중...</div>
        ) : requests.length === 0 ? (
          <div style={{ textAlign:'center', padding:'30px 0', color:'#aaa' }}>대기 중인 삭제 요청이 없습니다.</div>
        ) : requests.map(r => (
          <div key={r.id} onClick={() => openDetail(r)}
            style={{ border:'1px solid #e5e7eb', borderRadius:10, padding:'14px 16px', marginBottom:10, cursor:'pointer' }}
            onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <div style={{ fontSize:14, fontWeight:600 }}>
              {r.category} · {(() => { try { return JSON.parse(r.model_no).join(', '); } catch { return r.model_no||'-'; } })()}
              <span style={{ fontWeight:400, color:'#888', marginLeft:6 }}>({formatNum(r.plan_qty)}개)</span>
            </div>
            <div style={{ fontSize:12, color:'#888', marginTop:4 }}>
              요청자: {r.requested_by} · {r.requested_at}
            </div>
            {r.reason && <div style={{ fontSize:13, color:'#555', marginTop:6 }}>사유: {r.reason}</div>}
            {/* 이 목록에서 바로도 승인/거부 가능 — 클릭 이벤트가 상세 열기로 전파되지 않도록 stopPropagation */}
            <div style={{ display:'flex', gap:8, marginTop:10 }} onClick={e => e.stopPropagation()}>
              <button onClick={() => handleApprove(r.id)}
                style={{ flex:1, padding:'8px', background:'#dc2626', color:'#fff', border:'none',
                  borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>
                ✓ 승인(삭제)
              </button>
              <button onClick={() => handleReject(r.id)}
                style={{ flex:1, padding:'8px', background:'#fff', color:'#666', border:'1px solid #ddd',
                  borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>
                거부
              </button>
            </div>
          </div>
        ))}
      </div>

      {viewing && (
        <StatusDetail
          plan={{ id: viewing.plan_id, category: viewing.category, model_no: viewing.model_no, plan_qty: viewing.plan_qty }}
          statusList={viewStatuses}
          isAdmin={isAdminView}
          isSuperAdmin={isSuperAdmin}
          user={user}
          zIndex={2100}
          onClose={() => setViewing(null)}
          onUpdate={() => {}}
          onDeleted={() => { setViewing(null); load(); onChanged(); }}
          approveRequest={{ onApprove: () => handleApprove(viewing.id), onReject: () => handleReject(viewing.id) }}
          showMsg={showMsg}
        />
      )}
    </div>
  );
}

function PlanActionModal({ plan, isAdmin, isSuperAdmin, onClose, onEdit, onDelete }) {
  const progress = plan.progress || '생산계획';
  const models = (() => { try { return JSON.parse(plan.model_no).join(', '); } catch { return plan.model_no||'-'; } })();
  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:340, maxWidth:'92vw' }}
        onClick={e => e.stopPropagation()}>

        {/* 헤더 */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <div>
            <h3 style={{ fontSize:16, fontWeight:700, margin:0 }}>
              <span style={{ color: plan.category==='자체생산'?'#185FA5':'#854F0B' }}>
                {plan.category}
              </span> 관리
            </h3>
            <div style={{ fontSize:12, color:'#888', marginTop:4 }}>
              {models}&nbsp;|&nbsp;계획수량: {(plan.plan_qty||0).toLocaleString()}개
            </div>
          </div>
          <button onClick={onClose}
            style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {/* 수정 — 최고관리자는 언제나 가능, 관리자는 생산승인 전까지만 */}
          {(isSuperAdmin || (isAdmin && !['생산승인','생산진행','생산완료'].includes(progress))) && (
            <button onClick={() => { onEdit(plan); onClose(); }}
              style={{ width:'100%', padding:'11px', background:'#E6F1FB', color:'#185FA5',
                border:'1px solid #185FA5', borderRadius:8, fontSize:14, fontWeight:600, cursor:'pointer' }}>
              ✏️ 수정
            </button>
          )}
          {/* 삭제 (최고관리자만) */}
          {isSuperAdmin && (
            <button onClick={() => { onDelete(plan.id); onClose(); }}
              style={{ width:'100%', padding:'11px', background:'#fee2e2', color:'#dc2626',
                border:'1px solid #dc2626', borderRadius:8, fontSize:14, fontWeight:600, cursor:'pointer' }}>
              🗑 삭제
            </button>
          )}
          <button onClick={onClose}
            style={{ width:'100%', padding:'9px', background:'none', border:'1px solid #ddd',
              borderRadius:8, fontSize:13, color:'#888', cursor:'pointer' }}>
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 보류 사유 모달 ─────────────────────────────────
function HoldModal({ info, onClose, onSaved }) {
  const [reason, setReason] = useState('');
  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.5)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:420, maxWidth:'92vw' }}
        onClick={e => e.stopPropagation()}>
        <h3 style={{ fontSize:16, fontWeight:600, margin:'0 0 16px' }}>⏸ 생산보류 사유 입력</h3>
        <div style={{ fontSize:13, color:'#555', marginBottom:10 }}>
          {info.plan.category} | {info.status.model_no} - {info.status.color}
        </div>
        <textarea
          placeholder="보류 사유를 입력해주세요..."
          value={reason}
          onChange={e => setReason(e.target.value)}
          style={{ width:'100%', height:100, padding:'10px 12px', border:'1px solid #ddd',
            borderRadius:8, fontSize:14, resize:'vertical', outline:'none', boxSizing:'border-box',
            marginBottom:16 }}
        />
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={onClose}
            style={{ flex:1, padding:'10px', background:'#f0f0f0', border:'none',
              borderRadius:8, fontSize:14, cursor:'pointer', color:'#555' }}>
            취소
          </button>
          <button onClick={() => { if(!reason.trim()){alert('사유를 입력해주세요.');return;} onSaved(reason); }}
            style={{ flex:1, padding:'10px', background:'#993556', color:'#fff',
              border:'none', borderRadius:8, fontSize:14, fontWeight:600, cursor:'pointer' }}>
            보류 처리
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 생산계획 등록/수정 폼 ─────────────────────────
// 모델명은 더 이상 하드코딩된 목록(MODELS)이 아니라, 재고관리에 실제 등록된 완제품(품번/품명)을
// 선택된 제품군 기준으로 불러와서 사용합니다. 색상도 그 완제품들이 재고관리 등록 당시 실제로
// 갖게 된 상세옵션만 보여주고(등록된 옵션이 하나도 없으면 기존 기본 색상 6종으로 대체),
// 이렇게 재고관리와 항상 같은 모델명/색상을 쓰게 되어 생산현황→재고 자동반영(model_no 매칭)도
// 항상 정확히 맞아떨어집니다.
function PlanForm({ editData, groupId, groups = [], onClose, onSaved }) {
  const isEdit = !!editData;
  const [form, setForm] = useState({
    category:   editData?.category   || '자체생산',
    start_date: editData?.start_date || '',
    end_date:   editData?.end_date   || '',
    manager:    editData?.manager    || (JSON.parse(sessionStorage.getItem('user')||'{}').name || ''),
    note:       editData?.note       || '',
  });
  const [formGroupId, setFormGroupId] = useState(editData?.group_id ?? groupId ?? null);
  // 모델명은 재고관리에 실제 등록된 완제품 중 하나를 드롭다운으로 고릅니다.
  const [selModel, setSelModel] = useState(editData ? (parseJSON(editData.model_no)[0] || '') : '');
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [loadingCatalog, setLoadingCatalog]   = useState(false);
  const [loading, setLoading]     = useState(false);

  // 사양(색상 등 상세옵션)별 생산수량 — 재고관리 "목록"으로 등록된 사양 중에서 고르고,
  // 사양마다 서로 다른 수량을 입력합니다. 수정 화면을 열면 예전에 저장해둔 spec_qty를
  // 그대로 불러오고(없으면 예전 colors만 있던 계획은 사양만 채우고 수량은 비워둡니다).
  const [specRows, setSpecRows] = useState(() => {
    if (editData?.spec_qty) {
      try {
        const obj = JSON.parse(editData.spec_qty);
        const rows = Object.entries(obj).map(([spec, qty]) => ({ spec, qty: String(qty) }));
        if (rows.length) return rows;
      } catch { /* 무시 */ }
    }
    if (editData) {
      const legacyColors = parseJSON(editData.colors);
      if (legacyColors.length) return legacyColors.map(c => ({ spec: c, qty: '' }));
    }
    return [{ spec:'', qty:'' }];
  });

  // 선택된 제품군의 완제품 목록(품번/품명 + 등록된 사양 옵션)을 불러옵니다.
  useEffect(() => {
    if (!formGroupId) { setCatalogProducts([]); return; }
    setLoadingCatalog(true);
    API.get('/productions/catalog-products', { params:{ group_id: formGroupId } })
      .then(r => setCatalogProducts(r.data))
      .catch(() => setCatalogProducts([]))
      .finally(() => setLoadingCatalog(false));
  }, [formGroupId]);

  // 선택된 모델이 속한 제품군의 완제품 "목록"(재고관리 목록추가/삭제)에 실제 등록된 옵션
  // 목록 — 하드코딩 없이 재고관리와 항상 동일합니다. 등록된 옵션이 하나도 없으면 빈 배열이며,
  // 이 경우 사용자에게 재고관리에서 먼저 옵션을 등록하도록 안내합니다.
  const availableSpecs = (() => {
    const product = catalogProducts.find(p => p.model_no === selModel);
    return product?.options || [];
  })();

  const addSpecRow    = () => setSpecRows(prev => [...prev, { spec:'', qty:'' }]);
  const removeSpecRow = (idx) => setSpecRows(prev => prev.length > 1 ? prev.filter((_,i)=>i!==idx) : prev);
  const updateSpecRow = (idx, field, val) =>
    setSpecRows(prev => prev.map((r,i)=> i===idx ? { ...r, [field]: val } : r));

  // 이미 다른 행에서 고른 사양은 다음 행 드롭다운에서 제외해서 같은 사양이 중복 등록되지 않게 합니다.
  const specOptionsFor = (idx) => {
    const picked = new Set(specRows.filter((r,i)=>i!==idx && r.spec).map(r=>r.spec));
    return availableSpecs.filter(c => !picked.has(c) || specRows[idx].spec === c);
  };

  const handleSubmit = async () => {
    if (!formGroupId) { alert('제품군을 선택해주세요.'); return; }
    if (!selModel)     { alert('모델명을 선택해주세요.'); return; }
    const cleanSpecs = specRows.filter(r => r.spec && Number(r.qty) > 0);
    if (!cleanSpecs.length) { alert('옵션과 수량을 입력해주세요.'); return; }
    setLoading(true);
    try {
      if (isEdit) {
        // 수정은 기존 계획 하나를 그대로 업데이트합니다(사양이 여러 개면 한 계획에 계속 유지).
        const specQtyObj = {};
        cleanSpecs.forEach(r => { specQtyObj[r.spec] = Number(r.qty); });
        const totalQty = cleanSpecs.reduce((acc,r) => acc + Number(r.qty), 0);
        const body = {
          ...form,
          model_no: [selModel],
          colors: cleanSpecs.map(r => r.spec),
          spec_qty: specQtyObj,
          plan_qty: totalQty,
          group_id: formGroupId,
        };
        await API.put('/productions/plans/'+editData.id, body);
      } else {
        // 신규 등록은 사양(색상)마다 완전히 별도의 생산계획으로 각각 등록합니다 — 이렇게 해야
        // 관리승인/생산승인, 이후 생산현황 전환도 사양별로 서로 독립적으로 진행할 수 있습니다
        // (한 계획에 여러 사양을 묶으면 항상 다 같이 승인/진행되어야 하는 문제가 있었습니다).
        await Promise.all(cleanSpecs.map(r => API.post('/productions/plans', {
          ...form,
          model_no: [selModel],
          colors: [r.spec],
          spec_qty: { [r.spec]: Number(r.qty) },
          plan_qty: Number(r.qty),
          group_id: formGroupId,
        })));
      }
      onSaved();
    } catch(err) { alert(err.response?.data?.message || '오류'); }
    finally { setLoading(false); }
  };

  const inp = { width:'100%', padding:'10px 12px', border:'1px solid #ddd', borderRadius:8,
    fontSize:14, boxSizing:'border-box', marginBottom:14, outline:'none' };
  const inpRow = { ...inp, marginBottom:0 };
  const lbl = { fontSize:13, color:'#333', fontWeight:600, display:'block', marginBottom:6 };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.5)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:520,
        maxWidth:'92vw', maxHeight:'90vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:22 }}>
          <h3 style={{ fontSize:18, fontWeight:700, margin:0 }}>{isEdit?'생산계획 수정':'생산계획 등록'}</h3>
          <button onClick={onClose}
            style={{ background:'none', border:'none', fontSize:22, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        {/* 구분 */}
        <label style={lbl}>구분</label>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:14 }}>
          {['자체생산','주문생산'].map(cat => (
            <button key={cat} onClick={() => setForm({...form, category:cat})}
              style={{ padding:'12px', fontSize:14, fontWeight:600, cursor:'pointer',
                border: form.category===cat?'2px solid #185FA5':'1.5px solid #ddd',
                borderRadius:10, background:form.category===cat?'#E6F1FB':'#fff',
                color:   form.category===cat?'#185FA5':'#666' }}>
              {form.category===cat ? '✓ ' : ''}{cat}
            </button>
          ))}
        </div>

        {/* 제품군 + 모델명 — 한 줄에 나란히, 둘 다 드롭다운으로 구성하고 항목은 재고관리에
            등록된 목록을 그대로 가져옵니다. 제품군을 바꾸면 이전 제품군 기준으로 골랐던
            모델명/사양은 더 이상 유효하지 않으므로 초기화합니다. */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:4 }}>
          <div>
            <label style={lbl}>제품군</label>
            <select value={formGroupId||''}
              onChange={e => {
                setFormGroupId(Number(e.target.value));
                setSelModel('');
                setSpecRows([{ spec:'', qty:'' }]);
              }}
              style={inp}>
              {groups.length === 0 && <option value="">제품군 없음</option>}
              {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>모델명</label>
            <select value={selModel}
              onChange={e => { setSelModel(e.target.value); setSpecRows([{ spec:'', qty:'' }]); }}
              disabled={loadingCatalog || !catalogProducts.length}
              style={inp}>
              <option value="">
                {loadingCatalog ? '불러오는 중...' : catalogProducts.length===0 ? '등록된 완제품 없음' : '선택'}
              </option>
              {catalogProducts.map(p => (
                <option key={p.id} value={p.model_no}>{p.model_no} · {p.name}</option>
              ))}
            </select>
          </div>
        </div>
        {!loadingCatalog && catalogProducts.length === 0 && (
          <div style={{ fontSize:12, color:'#aaa', background:'#f8fafc', borderRadius:8,
            padding:'10px 12px', marginBottom:14 }}>
            이 제품군에 재고관리로 등록된 완제품이 없습니다. 먼저 재고관리에서 완제품을 등록해주세요.
          </div>
        )}

        {/* 옵션(색상 등 상세옵션) + 수량 — 제품군/모델명, 시작일/종료일과 동일하게 1:1 그리드로
            맞춰서 좌우 입력칸 경계가 한 줄로 정렬되게 합니다. 옵션이 여러 개면 수량 옆의 + 버튼으로
            행을 추가하며(+/- 버튼은 오른쪽 칸 안쪽에 배치해 전체 그리드 폭에 영향을 주지 않음),
            옵션 목록은 재고관리 "목록"으로 등록된 항목 그대로입니다. */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:6 }}>
          <label style={lbl}>옵션</label>
          <label style={lbl}>수량</label>
        </div>
        {specRows.map((row, idx) => (
          <div key={idx} style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:8 }}>
            <select value={row.spec} onChange={e => updateSpecRow(idx, 'spec', e.target.value)}
              disabled={!selModel || !availableSpecs.length} style={inpRow}>
              <option value="">
                {!selModel ? '모델명을 먼저 선택하세요' : !availableSpecs.length ? '등록된 옵션 없음' : '옵션 선택'}
              </option>
              {specOptionsFor(idx).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <div style={{ display:'flex', gap:8 }}>
              <input type="number" placeholder="수량" value={row.qty}
                onChange={e => updateSpecRow(idx, 'qty', e.target.value)} style={{ ...inpRow, flex:1, minWidth:0 }} />
              {idx === specRows.length - 1 ? (
                <button onClick={addSpecRow} title="옵션 추가" type="button"
                  style={{ flex:'0 0 38px', width:38, height:38, borderRadius:8, border:'1.5px solid #185FA5', background:'#E6F1FB',
                    color:'#185FA5', fontSize:18, fontWeight:700, cursor:'pointer', lineHeight:1 }}>
                  +
                </button>
              ) : (
                <button onClick={() => removeSpecRow(idx)} title="옵션 삭제" type="button"
                  style={{ flex:'0 0 38px', width:38, height:38, borderRadius:8, border:'1.5px solid #ddd', background:'#fff',
                    color:'#999', fontSize:16, fontWeight:700, cursor:'pointer', lineHeight:1 }}>
                  ×
                </button>
              )}
            </div>
          </div>
        ))}
        {selModel && !availableSpecs.length && (
          <div style={{ fontSize:12, color:'#aaa', background:'#f8fafc', borderRadius:8,
            padding:'10px 12px', marginBottom:8 }}>
            이 제품군에 재고관리로 등록된 옵션이 없습니다. 먼저 재고관리에서 옵션을 등록해주세요.
          </div>
        )}
        <div style={{ marginBottom:6 }} />

        {/* 날짜 */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <div>
            <label style={lbl}>시작일</label>
            <input type="date" value={form.start_date}
              onChange={e => setForm({...form, start_date:e.target.value})} style={inp} />
          </div>
          <div>
            <label style={lbl}>종료일</label>
            <input type="date" value={form.end_date}
              onChange={e => setForm({...form, end_date:e.target.value})} style={inp} />
          </div>
        </div>

        {/* 비고 */}
        <label style={lbl}>특이사항</label>
        <textarea value={form.note} onChange={e => setForm({...form, note:e.target.value})}
          placeholder="특이사항 입력..."
          style={{ ...inp, height:80, resize:'vertical' }} />

        <button onClick={handleSubmit} disabled={loading}
          style={{ width:'100%', padding:'13px', background:loading?'#aaa':'#185FA5', color:'#fff',
            border:'none', borderRadius:10, fontSize:15, fontWeight:700,
            cursor:loading?'not-allowed':'pointer' }}>
          {loading?'저장 중...':(isEdit?'수정 완료':'등록 완료')}
        </button>
      </div>
    </div>
  );
}

// ── 생산현황 상세 모달 ────────────────────────────
// approveRequest: 삭제요청 목록(DeleteRequestsModal)에서 리스트 항목을 클릭해 이 상세를 열었을 때만
// 전달되는 { id, onApprove, onReject } — 있으면 상단에 "삭제승인"/"거부" 버튼을 추가로 보여준다.
// zIndex: 삭제요청 목록 위에 겹쳐서 열릴 때는 그 목록보다 높은 값을 넘겨받아, 이 상세 모달의
// 바깥을 클릭했을 때 이 상세만 닫히고(오버레이가 클릭을 가로채므로) 뒤에 있는 목록은 그대로 남는다.
function StatusDetail({ plan, statusList, isAdmin, isSuperAdmin, user, onClose, onConfirmDone, onUpdate, onDeleted, onEdit, showMsg, approveRequest, zIndex }) {
  const { hasDetail } = usePermissions();
  // 삭제요청(승인 필요)과 즉시삭제(승인 없이 바로 삭제)는 계정관리 > 권한설정 > 생산관리
  // 쓰기권한에서 서로 다른 별개의 권한으로 관리합니다.
  const canRequestDelete = hasDetail('productions', 'status_delete_allowed');   // "생산현황 삭제요청"
  const canDeleteDirect  = hasDetail('productions', 'status_delete_action');    // "생산현황 삭제"
  const [statuses, setStatuses] = useState(statusList);
  const [logs, setLogs]         = useState({});
  const [deleting, setDeleting] = useState(false);
  // 이미 대기 중인 삭제요청이 있으면(GET /plans가 delete_requested_at을 함께 내려줌) 그 일시를
  // 버튼 옆에 표시하고, 요청 버튼도 다시 못 누르게(중복 요청 방지) 비활성화합니다.
  const [requestedAt, setRequestedAt] = useState(plan?.delete_requested_at || null);
  useEffect(() => { setRequestedAt(plan?.delete_requested_at || null); }, [plan?.delete_requested_at]);

  const loadLogs = async (id) => {
    const res = await API.get('/productions/status/'+id+'/logs');
    setLogs(p => ({ ...p, [id]: res.data }));
  };
  useEffect(() => { statuses.forEach(s => loadLogs(s.id)); }, []);

  // 생산완료 확인 처리 (confirmed=1 로 표시 → 목록에서 숨김, DB는 유지)
  const handleConfirm = async () => {
    if (!window.confirm('생산완료를 확인하시겠습니까?\n확인된 항목은 생산현황 목록에서 제거되고 누적생산에 포함됩니다.')) return;
    try {
      // 해당 plan의 모든 완료 status를 confirmed 처리
      const doneStatuses = statuses.filter(s => s.status === '생산완료');
      if (doneStatuses.length === 0) {
        showMsg('생산완료된 항목이 없습니다.', 'error');
        return;
      }
      await Promise.all(doneStatuses.map(s =>
        API.patch('/productions/status/'+s.id+'/confirm')
      ));
      showMsg('생산완료 확인되었습니다! 목록에서 제거됩니다.');
      // 부모에서 모달 닫기 + 데이터 새로고침
      if (onConfirmDone) onConfirmDone();
      else { onUpdate(); onClose(); }
    } catch(err) {
      showMsg(err.response?.data?.message || '오류 발생', 'error');
    }
  };

  const isOwner = plan?.created_by === (user?.name);

  // 최고관리자, 또는 "생산현황 삭제" 권한을 받은 사람: 승인 절차 없이 즉시 삭제
  // (생산계획+생산현황+생산이력 모두 함께 삭제됨)
  const handleDelete = async () => {
    if (!window.confirm('정말 삭제하시겠습니까?\n생산계획과 연결된 생산현황·이력이 모두 함께 삭제됩니다. 되돌릴 수 없습니다.')) return;
    setDeleting(true);
    try {
      await API.delete('/productions/plans/'+plan.id);
      showMsg('삭제 완료! 생산현황에서도 함께 삭제되었습니다.');
      if (onDeleted) onDeleted();
      else { onUpdate(); onClose(); }
    } catch (err) {
      showMsg(err.response?.data?.message || '오류가 발생했습니다.', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // "생산현황 삭제요청" 권한을 받은 등록자 본인: 삭제 요청만 (최고관리자 승인 필요)
  // 확인창을 한 번 더 띄우고, 최종 확인되면 요청일시를 버튼 왼쪽에 바로 표시합니다(모달은 닫지
  // 않고 그대로 유지 — 요청이 접수됐다는 걸 화면에서 바로 확인할 수 있도록).
  const handleDeleteRequest = async () => {
    if (!window.confirm('삭제를 요청하시겠습니까?\n최고관리자가 승인해야 실제로 삭제됩니다.')) return;
    setDeleting(true);
    try {
      const res = await API.post('/productions/plans/'+plan.id+'/delete-request', {});
      setRequestedAt(res.data?.requested_at || new Date().toISOString());
      showMsg('삭제 요청이 접수되었습니다. 최고관리자 승인 후 삭제됩니다.');
      if (onUpdate) onUpdate();
    } catch (err) {
      showMsg(err.response?.data?.message || '오류가 발생했습니다.', 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.5)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:zIndex||1000 }}>
      <div style={{ background:'#fff', borderRadius:16, width:760, maxWidth:'95vw',
        maxHeight:'88vh', display:'flex', flexDirection:'column', overflow:'hidden' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ padding:'20px 24px', borderBottom:'1px solid #f0f0f0', flexShrink:0,
          display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
              <h3 style={{ fontSize:18, fontWeight:600, margin:0 }}>📊 생산현황 상세</h3>
              {/* 삭제요청 목록에서 열었을 때만: 이 화면에서 바로 삭제요청을 승인/거부 */}
              {approveRequest && (
                <>
                  <button onClick={approveRequest.onApprove}
                    style={{ fontSize:12, padding:'5px 14px', background:'#dc2626', color:'#fff',
                      border:'none', borderRadius:8, cursor:'pointer', fontWeight:600 }}>
                    ✓ 삭제승인
                  </button>
                  <button onClick={approveRequest.onReject}
                    style={{ fontSize:12, padding:'5px 14px', background:'#fff', color:'#666',
                      border:'1px solid #ddd', borderRadius:8, cursor:'pointer', fontWeight:600 }}>
                    거부
                  </button>
                </>
              )}
              {/* 생산완료 확인 버튼 — 관리자만, 완료 항목 있을 때만 */}
              {isAdmin && statuses.some(s => s.status === '생산완료') && (
                <button onClick={handleConfirm}
                  style={{ fontSize:12, padding:'5px 14px', background:'#0F6E56', color:'#fff',
                    border:'none', borderRadius:8, cursor:'pointer', fontWeight:600 }}>
                  ✅ 생산완료 확인
                </button>
              )}
              {/* 수정 — 최고관리자는 언제나 가능, 관리자는 생산승인 전까지만 */}
              {onEdit && (isSuperAdmin || (isAdmin && !['생산승인','생산진행','생산완료'].includes(plan?.progress||'생산계획'))) && (
                <button onClick={() => onEdit(plan)}
                  style={{ fontSize:12, padding:'5px 14px', background:'#fff', color:'#185FA5',
                    border:'1px solid #185FA5', borderRadius:8, cursor:'pointer', fontWeight:600 }}>
                  ✏️ 수정
                </button>
              )}
              {/* 삭제 — 최고관리자이거나 "생산현황 삭제" 권한이 있으면 승인 없이 즉시삭제.
                  그렇지 않고 본인이 등록한 계획이면서 "생산현황 삭제요청" 권한이 있으면
                  최고관리자 승인이 필요한 삭제요청 버튼을 우측 상단(닫기 버튼 왼쪽)에 표시.
                  단, 삭제요청 목록에서 열린 경우(approveRequest)는 위의 삭제승인/거부 버튼과
                  중복되므로 이 블록은 표시하지 않는다. */}
              {approveRequest ? null : (isSuperAdmin || canDeleteDirect) ? (
                <button onClick={handleDelete} disabled={deleting}
                  style={{ fontSize:12, padding:'5px 14px', background:'#fff', color:'#dc2626',
                    border:'1px solid #dc2626', borderRadius:8, cursor:deleting?'not-allowed':'pointer', fontWeight:600 }}>
                  🗑 삭제
                </button>
              ) : isOwner && canRequestDelete && (
                <>
                  {/* 이미 대기 중인 삭제요청이 있으면 그 일시를 버튼 왼쪽에 표시하고,
                      중복 요청을 막기 위해 버튼을 비활성화한다. */}
                  {requestedAt && (
                    <span style={{ fontSize:12, color:'#92400E' }}>
                      삭제요청 일시: {formatDT(requestedAt)}
                    </span>
                  )}
                  <button onClick={handleDeleteRequest} disabled={deleting || !!requestedAt}
                    style={{ fontSize:12, padding:'5px 14px',
                      background: requestedAt ? '#f3f4f6' : '#fff',
                      color: requestedAt ? '#aaa' : '#92400E',
                      border:`1px solid ${requestedAt ? '#ddd' : '#92400E'}`, borderRadius:8,
                      cursor:(deleting || requestedAt) ? 'not-allowed':'pointer', fontWeight:600 }}>
                    🗑 {requestedAt ? '삭제요청됨' : '삭제요청'}
                  </button>
                </>
              )}
            </div>
            <p style={{ fontSize:14, color:'#888', margin:'4px 0 0' }}>
              {plan?.category} | 계획수량: {formatNum(plan?.plan_qty)}
              {getOriginalRemark(plan?.note) && <> | 특이사항 : {getOriginalRemark(plan?.note)}</>}
            </p>
          </div>
          <button onClick={onClose}
            style={{ background:'none', border:'none', fontSize:22, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        <div style={{ overflowY:'auto', flex:1, padding:'16px 24px' }}>
          {statuses.map(s => (
            <div key={s.id} style={{ background:'#f8fafc', borderRadius:12,
              border:'1px solid #e5e7eb', marginBottom:16, overflow:'hidden' }}>
              <div style={{ background:'#185FA5', padding:'10px 16px', display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ color:'#fff', fontWeight:700, fontSize:15 }}>{s.model_no}</span>
                <span style={{ padding:'2px 10px', borderRadius:20, fontWeight:600, fontSize:12,
                  background:COLOR_STYLE[s.color]?.bg||'#ddd', color:COLOR_STYLE[s.color]?.color||'#fff' }}>
                  {s.color}
                </span>
                <span style={{ fontSize:11, padding:'2px 8px', borderRadius:20,
                  background:s.status==='생산완료'?'#E1F5EE':'rgba(255,255,255,0.2)',
                  color:s.status==='생산완료'?'#0F6E56':'#fff' }}>
                  {s.status}
                </span>
                <span style={{ marginLeft:'auto', color:'rgba(255,255,255,0.7)', fontSize:12 }}>담당: {s.manager||'-'}</span>
              </div>
              <div style={{ padding:16 }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:10 }}>
                  {[['목표수량',formatNum(s.target_qty),'#1a1a1a'],
                    ['생산수량',formatNum(s.produced_qty),'#185FA5'],
                    ['공정률',s.progress_rate+'%',s.progress_rate>=100?'#0F6E56':'#185FA5']].map(([l,v,c])=>(
                    <div key={l} style={{ background:'#fff', borderRadius:8, padding:'10px', textAlign:'center', border:'1px solid #e5e7eb' }}>
                      <div style={{ fontSize:11, color:'#888', marginBottom:2 }}>{l}</div>
                      <div style={{ fontSize:20, fontWeight:700, color:c }}>{v}</div>
                    </div>
                  ))}
                </div>
                <div style={{ background:'#e5e7eb', borderRadius:6, height:8, marginBottom:8, overflow:'hidden' }}>
                  <div style={{ background:s.progress_rate>=100?'#0F6E56':'#185FA5',
                    width:Math.min(s.progress_rate,100)+'%', height:8, borderRadius:6 }} />
                </div>

                {/* D-Day / 완료일 표시 */}
                <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12,
                  padding:'10px 14px', background:'#f8fafc', borderRadius:8, flexWrap:'wrap' }}>
                  <span style={{ fontSize:13, color:'#666' }}>목표일: <strong>{s.end_date||'-'}</strong></span>
                  {s.completed_at ? (
                    <>
                      {/* 완료: 실제완료일 + 목표대비 차이 */}
                      <span style={{ fontSize:13, color:'#0F6E56', fontWeight:600 }}>
                        ✅ 완료일: {parseDate(s.completed_at)}
                      </span>
                      {(() => {
                        const diff = calcDday2(s.end_date, parseDate(s.completed_at));
                        const early = diff > 0;
                        const label = diff===0?'당일완료':early?`D-${diff}`:`D+${Math.abs(diff)}`;
                        const color = early||diff===0?'#185FA5':'#dc2626';
                        const bg    = early||diff===0?'#E6F1FB':'#fee2e2';
                        return (
                          <span style={{ fontSize:12, padding:'2px 10px', borderRadius:20,
                            background:bg, color, fontWeight:700 }}>{label}</span>
                        );
                      })()}
                    </>
                  ) : (
                    <>
                      {/* 진행중: 목표일 기준 D-Day */}
                      <span style={{ fontSize:12, color:'#888' }}>진행중:</span>
                      <DdayBadge date={s.end_date} />
                    </>
                  )}
                </div>

                <div style={{ borderTop:'1px solid #e5e7eb', paddingTop:10 }}>
                  <div style={{ fontSize:12, fontWeight:600, color:'#888', marginBottom:8 }}>📋 업데이트 이력</div>

                  {/* 생산계획/관리승인/생산승인 이력 */}
                  <div style={{ background:'#f0f6ff', borderRadius:8, padding:'8px 12px', marginBottom:10 }}>
                    {/* 생산계획 등록 이력 */}
                    {plan?.created_by && (
                      <div style={{ fontSize:12, color:'#555', marginBottom:4, display:'flex', gap:6, alignItems:'center' }}>
                        <span style={{ minWidth:60, fontWeight:600 }}>⚪ 생산계획:</span>
                        <strong>{plan.created_by}</strong>
                        <span style={{ color:'#aaa' }}>{formatDT(plan.created_at)}</span>
                      </div>
                    )}
                    {plan?.admin_confirmed_by && (
                      <div style={{ fontSize:12, color:'#185FA5', marginBottom:4, display:'flex', gap:6, alignItems:'center' }}>
                        <span style={{ minWidth:60, fontWeight:600 }}>🔵 관리승인:</span>
                        <strong>{plan.admin_confirmed_by}</strong>
                        <span style={{ color:'#aaa' }}>{formatDT(plan.admin_confirmed_at)}</span>
                      </div>
                    )}
                    {plan?.worker_confirmed_by && (
                      <div style={{ fontSize:12, color:'#0F6E56', display:'flex', gap:6, alignItems:'center' }}>
                        <span style={{ minWidth:60, fontWeight:600 }}>🟢 생산승인:</span>
                        <strong>{plan.worker_confirmed_by}</strong>
                        <span style={{ color:'#aaa' }}>{formatDT(plan.worker_confirmed_at)}</span>
                      </div>
                    )}
                  </div>

                  {(logs[s.id]||[]).length === 0 ? (
                    <div style={{ fontSize:12, color:'#ccc' }}>이력 없음</div>
                  ) : (logs[s.id]||[]).map(log => (
                    <div key={log.id} style={{ fontSize:12, color:'#555', marginBottom:4,
                      display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                      <span style={{ color:'#aaa', minWidth:130 }}>{log.updated_at}</span>
                      <span style={{ fontWeight:600, color:'#185FA5' }}>{log.updated_by}</span>
                      <span>— 생산수량</span>
                      <span style={{ color:'#888' }}>{formatNum(log.before_qty)}</span>
                      <span>→</span>
                      <span style={{ fontWeight:700, color:log.after_qty>log.before_qty?'#0F6E56':'#dc2626' }}>
                        {formatNum(log.after_qty)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}